import axios from 'axios';
import store from '@/store';
import imageService from '../imageService';
import apiService from '../apiService';
import workflowService from '../workflowService';
import html2canvas from 'html2canvas';
import { withTimeout, H2C_TIMEOUT_MS, cloneOnly } from '@/utils/withTimeout';
import { logServiceError, logS3UploadError, logAIRequestError, ERROR_TYPES } from './errorLoggingService';
import { judgeOcrResult, hasNoFallbackTranslation } from '@/utils/imageTranslateResult';
import { setStepCount } from './workProgress';
import { stripAltHan } from './detailAltText';
import { guardDetailTranslations, claimBasisOf, buildClaimWarning } from './detailClaimGuard';
import { zeroDetailImageResult } from './detailImageZero';

/**
 * 상세 이미지 처리를 위한 서비스
 */
const detailImageService = {
  /**
   * 단일 이미지 번역 처리 - OCR 및 AI 텍스트 번역 통합 (중국어→한국어)
   * @param {string} imageUrl - 번역할 이미지 URL
   * @param {number} index - 이미지 인덱스
   * @param {Object} [claimBasis] - 소재 대조 근거 {attributes, options, productFeature, targetKeyword}. 없으면 인증·최상급만 본다
   * @returns {Promise<Object>} - 번역 결과 객체
   */
  async translateSingleImage(imageUrl, index, claimBasis) {
    if (!imageUrl) {
      console.error('[디버깅] translateSingleImage: 이미지 URL이 없습니다');
      throw new Error('이미지 URL이 없습니다');
    }

    console.log(`[디버깅] translateSingleImage 시작 - 인덱스: ${index}, URL: ${imageUrl.substring(0, 50)}...`);
    console.trace('[디버깅] translateSingleImage 호출 스택 추적');

    try {
      console.log(`이미지 번역 시작, 인덱스 ${index}:`, imageUrl.substring(0, 50) + '...');

      // 1. 이미지 번역 API 호출 - getImageTranslate (중국어→한국어)
      const apiStartTime = Date.now();
      const res = await axios.post('/api/aiPage/getImageTranslateV2', [{ imgUrl: imageUrl }]);
      const apiEndTime = Date.now();

      console.log(`번역 API 응답 시간: ${apiEndTime - apiStartTime}ms`);

      if (res.data.요청결과 !== '성공' || !res.data.result || res.data.result.length === 0) {
        throw new Error('이미지 번역 API 응답 실패: ' + (res.data.error || '알 수 없는 오류'));
      }

      // 번역 API 결과
      const transInfo = res.data.result[0];
      console.log('번역 API 응답 데이터:', transInfo);

      // 번역 결과에서 RequestId 추출
      const requestId = transInfo.RequestId || transInfo.requestId || '';

      // 로깅을 위해 API에서 제공한 URL들 저장 (사용하지는 않음)
      const apiProvidedUrl = transInfo.Data?.SslUrl || transInfo.Data?.Url;
      console.log('API 제공 URL (로그용):', apiProvidedUrl ? apiProvidedUrl.substring(0, 50) + '...' : '없음');

      // 항상 원본 이미지 URL 사용 (API 응답 URL 사용 안함 - CORS/403 문제 해결)
      const translatedImgUrl = imageUrl; // 항상 원본 이미지만 사용

      // RequestId 없는 경우 OCR 처리 생략
      if (!requestId) {
        console.warn('RequestId가 없어서 OCR 처리를 생략합니다.');

        // ⚠ translatedImgUrl 은 원본 URL 이다 — 번역 결과를 못 받았다. process() 가 재시도·경고하도록 표시한다
        const processedImage = {
          index: index,
          url: translatedImgUrl,
          requestId: requestId,
          originalUrls: [imageUrl],
          processType: 'translated',
          translationData: transInfo,
          groupSize: 1,
          lastEdited: new Date(),
          isTranslated: translatedImgUrl !== imageUrl,
          translationFailed: true,
          failReason: `번역 요청 번호 없음${transInfo && transInfo.Code ? ` (Code ${transInfo.Code}${transInfo.Message ? ' ' + transInfo.Message : ''})` : ''}`
        };

        return processedImage;
      }

      // 2. OCR 데이터 조회 (배치 쿼리) - getImageTranslateBatchQuery
      console.log(`OCR 정보 조회 시작 - RequestId: ${requestId}`);

      try {
        // 배치 쿼리 API 호출
        const result = await this.getImageTranslateBatchQuery([requestId]);
        console.log('배치 쿼리 결과:', result);

        if (!result || !result.Data || !result.Data.Content) {
          throw new Error('OCR 데이터 조회 결과가 없거나 유효하지 않습니다.');
        }

        // OCR 정보 (텍스트)
        const ocrInfo = result.Data.Content[requestId];
        const ocrState = judgeOcrResult(ocrInfo);
        if (ocrState !== 'hasText') {
          // noText = 정상 응답인데 글자가 없다(원래 번역할 게 없는 이미지) → 원본 그대로가 정상
          // missing/error = 결과를 못 받았다 → 원본(미번역)이므로 process() 가 재시도·경고하도록 표시한다
          const ocrFailed = ocrState !== 'noText';
          console.warn(`OCR 정보가 없습니다. (${ocrState})`, ocrInfo && ocrInfo.Code, ocrInfo && ocrInfo.Message);

          const processedImage = {
            index: index,
            url: translatedImgUrl,
            requestId: requestId,
            originalUrls: [imageUrl],
            processType: 'translated',
            translationData: transInfo,
            groupSize: 1,
            lastEdited: new Date(),
            isTranslated: translatedImgUrl !== imageUrl
          };
          if (ocrFailed) {
            processedImage.translationFailed = true;
            processedImage.failReason = ocrState === 'missing'
              ? 'OCR 결과 없음(조회 시점에 결과가 없음)'
              : `OCR 결과 오류 (Code ${ocrInfo.Code}${ocrInfo.Message ? ' ' + ocrInfo.Message : ''})`;
          }

          return processedImage;
        }

        // 3. 텍스트 번역을 AI로 개선 - getAITranslate (확장 통신으로 구현됨)
        console.log('OCR 추출 텍스트:', ocrInfo.Orc.map(e => e.source).join(', '));

        try {
          // AI 모델에 전송할 데이터 구성
          const chineseSources = ocrInfo.Orc.map(e => e.source);

          // AI 번역 요청 (확장 통신으로 구현됨) - OCR 정보 전달
          const aiResult = await this.getAITranslate(imageUrl, chineseSources, ocrInfo);
          console.log('AI 번역 결과:', aiResult);

          // 번역 문구의 소재·인증·최상급을 원문·1688 속성과 대조해 고친다(PR261016-11F5C4) — 바꾼 칸은 경고로 남긴다
          const claimWarnings = guardDetailTranslations(aiResult, claimBasis || null);
          if (claimWarnings.length) console.warn(`[상세이미지] 근거 없는 문구 정정 ${claimWarnings.length}건 (인덱스 ${index}):`, claimWarnings);

          if (aiResult && aiResult.length > 0) {
            // OCR 데이터에 AI 번역 결과 적용
            ocrInfo.AITranslate = aiResult;

            // OCR 데이터 가공 (id 제거 및 타겟 텍스트, 폰트 사이즈, bounding box 설정)
            const ocrData = ocrInfo.Orc.map(e => {
              const obj = JSON.parse(JSON.stringify(e)); // 깊은 복사
              delete obj.id;

              // AI 번역 결과에서 일치하는 항목 찾기
              const aiMatch = aiResult.find(f => f.ch === e.source);

              // 번역 결과, 폰트 사이즈, bounding box 설정
              if (aiMatch) {
                // 번역된 텍스트 적용 (이모지 제거 필터 적용)
                let translatedText = aiMatch.ko[0] || e.target;
                obj.target = this.removeEmojis(translatedText);

                // 폰트 사이즈가 추천됐다면 적용 (font_size 필드가 있는지 확인)
                if (aiMatch.font_size && typeof aiMatch.font_size === 'number') {
                  // 숫자만 받아 px 단위 추가하여 설정
                  obj.font_size = `${aiMatch.font_size}px`;
                  console.log(`폰트 사이즈 변경: "${e.source}" - ${e.font_size} → ${obj.font_size}`);
                }

                // 텍스트 길이 기반 스마트 bounding box 조정
                try {
                  if (obj.bounding_box) {
                    // 원본 및 번역 텍스트 길이
                    const sourceLength = e.source.length;
                    const targetLength = obj.target.length;

                    // 텍스트 길이 비율 계산 (한국어:중국어)
                    const textLengthRatio = targetLength / sourceLength;

                    // bounding box 문자열을 배열로 파싱
                    let boxCoords = JSON.parse(obj.bounding_box.replace(/\s+/g, ''));

                    if (Array.isArray(boxCoords) && boxCoords.length >= 4) {
                      // 기존 bounding box 크기 계산
                      const x1 = boxCoords[0][0]; // 좌상단 x
                      const y1 = boxCoords[0][1]; // 좌상단 y
                      const x2 = boxCoords[1][0]; // 우상단 x
                      // 우하단 y 좌표 (높이 계산용)
                      const y3 = boxCoords[2][1];

                      // 너비와 높이 계산
                      const width = Math.abs(x2 - x1);
                      const height = Math.abs(y3 - y1);

                      // 중심점 계산
                      const centerX = x1 + width / 2;
                      const centerY = y1 + height / 2;

                      // 정렬 방식 고려
                      const alignment = obj.align || 'left';

                      // 줄 수 고려
                      const lineCount = parseInt(obj.line_count) || 1;

                      // 조정 계산 로직
                      let widthPercent = 100;
                      let heightPercent = 100;

                      // 1. AI 제안 값이 있으면 우선 사용
                      if (aiMatch.adjust_box) {
                        widthPercent = aiMatch.adjust_box.width_percent || 100;
                        heightPercent = aiMatch.adjust_box.height_percent || 100;
                      }
                      // 2. AI 제안 없으면 텍스트 길이 비율로 자동 계산
                      else {
                        // 텍스트 길이 차이에 따른 너비 조정
                        // - 한국어가 중국어보다 30% 이상 길면 박스 확장
                        // - 한국어가 중국어보다 30% 이상 짧으면 박스 축소
                        // - 수직 텍스트(줄수 > 1)는 덜 확장
                        if (textLengthRatio > 1.3) {
                          // 너비 확장 (한국어가 더 긴 경우)
                          widthPercent = Math.min(150, Math.round(textLengthRatio * 100));

                          // 줄 수가 1보다 크면 높이도 조정 필요할 수 있음
                          if (lineCount > 1) {
                            heightPercent = Math.min(140, 100 + (widthPercent - 100) / 2);
                          }
                        } else if (textLengthRatio < 0.7) {
                          // 너비 축소 (한국어가 더 짧은 경우)
                          // 그러나 너무 많이 축소하지 않음 (최소 80%)
                          widthPercent = Math.max(80, Math.round(textLengthRatio * 100));
                        }

                        // 폰트 사이즈가 특히 큰 경우(48px 이상), 너비 추가 조정
                        const fontSize = parseInt(obj.font_size) || 0;
                        if (fontSize >= 48 && widthPercent > 100) {
                          widthPercent += 10; // 큰 폰트는 추가 공간 필요
                        }
                      }

                      // 새 너비/높이 계산
                      let newWidth = width * (widthPercent / 100);
                      let newHeight = height * (heightPercent / 100);

                      // 정렬에 따른 좌표 계산
                      let newX1, newX2;

                      if (alignment === 'center') {
                        // 중앙 정렬 - 중심점 유지
                        newX1 = centerX - newWidth / 2;
                        newX2 = centerX + newWidth / 2;
                      } else if (alignment === 'right') {
                        // 우측 정렬 - 우측 좌표 유지
                        newX2 = x2;
                        newX1 = newX2 - newWidth;
                      } else {
                        // 좌측 정렬 (기본값) - 좌측 좌표 유지
                        newX1 = x1;
                        newX2 = newX1 + newWidth;
                      }

                      // 세로 위치 (높이) 계산
                      const newY1 = centerY - newHeight / 2;
                      const newY3 = centerY + newHeight / 2;

                      // 새로운 bounding box 생성
                      const newBoxCoords = [
                        [Math.round(newX1), Math.round(newY1)], // 좌상단
                        [Math.round(newX2), Math.round(newY1)], // 우상단
                        [Math.round(newX2), Math.round(newY3)], // 우하단
                        [Math.round(newX1), Math.round(newY3)]  // 좌하단
                      ];

                      // bounding box 문자열 생성
                      obj.bounding_box = JSON.stringify(newBoxCoords).replace(/\],\[/g, '], [');

                      // 조정 로그
                      console.log(`Bounding box 스마트 조정: "${e.source}" -> "${obj.target}" - 비율(한:중): ${textLengthRatio.toFixed(2)}, 너비: ${widthPercent}%, 높이: ${heightPercent}%, 정렬: ${alignment}`);
                    }
                  }
                } catch (error) {
                  console.warn(`Bounding box 조정 실패: "${e.source}" - ${error.message}`);
                }
              } else {
                // AI 매칭 결과가 없으면 원본 타겟 유지
                obj.target = e.target;
              }

              return obj;
            });

            // 4. 번역된 텍스트로 최종 이미지 생성 - getImageOCR

            const ocrParams = {
              preRequestId: requestId,
              url: transInfo.Data?.Url || '',
              rmUrl: transInfo.Data?.RmUrl || '',
              ocrData: ocrData
            };
            console.log('[디버깅] OCR 파라미터:', ocrParams);

            // OCR API 호출 - 새 이미지 생성
            console.log('[디버깅] getImageOCR API 호출 시작');
            const transInfoOCR = await axios.post('/api/aiPage/getImageOCR', ocrParams);
            console.log('[디버깅] OCR 이미지 생성 결과:', transInfoOCR.data);

            if (transInfoOCR.data.요청결과 === '성공' && transInfoOCR.data.result) {
              // 최종 이미지 URL 및 정보 가져오기
              const finalRequestId = transInfoOCR.data.result.RequestId;
              let finalUrl = transInfoOCR.data.result.Data?.SslUrl ||
                transInfoOCR.data.result.Data?.Url ||
                translatedImgUrl;

              // HTTP URL을 HTTPS로 자동 변환 (Mixed Content 오류 방지)
              if (finalUrl && finalUrl.startsWith('http://')) {
                finalUrl = finalUrl.replace('http://', 'https://');
                console.log('[디버깅] HTTP → HTTPS 자동 변환:', finalUrl);
              }

              console.log('[디버깅] OCR 생성 후 최종 URL 정보:', {
                finalRequestId: finalRequestId || 'none',
                finalUrl: finalUrl ? finalUrl.substring(0, 50) + '...' : 'none',
                isHttpUrl: finalUrl ? finalUrl.startsWith('http') : false
              });

              // URL 접근성 체크 (S3 업로드 필요 여부 결정 용도)
              let needsS3Upload = false;
              if (finalUrl && finalUrl.startsWith('http')) {
                try {
                  // 가벼운 HEAD 요청으로 URL 접근 가능한지 테스트 (실제 다운로드 없음)
                  console.log('[디버깅] OCR URL 접근성 확인 중...');
                  const response = await fetch(finalUrl, {
                    method: 'HEAD',
                    cache: 'no-cache',
                    timeout: 3000
                  });
                  const isAccessible = response.ok;
                  console.log(`[디버깅] OCR URL 접근 결과: ${isAccessible ? '성공' : '실패'} (${response.status})`);

                  // 접근 불가능하면 (403 등) S3 업로드 필요
                  needsS3Upload = !isAccessible;
                } catch (err) {
                  console.log(`[디버깅] OCR URL 접근성 확인 오류: ${err.message}`);
                  needsS3Upload = true; // 오류 발생하면 S3 업로드 필요
                }
              } else {
                console.log('[디버깅] OCR API가 유효한 URL을 반환하지 않았습니다. Base64로 반환하겠습니다.');
                needsS3Upload = true;
              }

              // 중요: URL로 반환하고 isBase64=false 설정 (필요시에만 base64로 변환)
              console.log(`[디버깅] 이미지 반환 형식: ${needsS3Upload ? 'Base64 필요' : 'URL 직접 사용'}`);

              // 최종 처리된 이미지 정보 반환
              const processedImage = {
                index: index,
                url: finalUrl,
                requestId: finalRequestId || requestId,
                originalUrls: [imageUrl],
                processType: 'translated',
                translationData: transInfoOCR.data.result,
                ocrInfo: JSON.parse(JSON.stringify(ocrInfo)), // 깊은 복사
                groupSize: 1,
                lastEdited: new Date(),
                isTranslated: true,
                isBase64: false, // URL로 반환 - S3 업로드는 process 함수 끝에서 한 번만 수행
                ...(claimWarnings.length ? { claimWarnings } : {})
              };

              return processedImage;
            } else {
              throw new Error('OCR 이미지 생성 실패: ' + (transInfoOCR.data.error || '알 수 없는 오류'));
            }
          } else {
            // AI 개선 결과가 없는 경우 일반 번역 결과 사용
            // ⚠ translatedImgUrl 은 원본 URL 이다 — 글자가 있는데 번역 결과가 비었다. process() 가 재시도·경고하도록 표시한다
            const processedImage = {
              index: index,
              url: translatedImgUrl,
              requestId: requestId,
              originalUrls: [imageUrl],
              processType: 'translated',
              translationData: transInfo,
              ocrInfo: JSON.parse(JSON.stringify(ocrInfo)), // 깊은 복사
              groupSize: 1,
              lastEdited: new Date(),
              isTranslated: translatedImgUrl !== imageUrl,
              translationFailed: true,
              failReason: 'AI 번역 결과 없음'
            };

            return processedImage;
          }
        } catch (aiError) {
          console.error('AI 번역 또는 OCR 생성 오류:', aiError);
          logAIRequestError('detailImageService', 'translateSingleImage', aiError, {
            fallbackAction: '기본 번역 결과 사용',
            additional: { imageIndex: index, requestId }
          });

          // AI 오류 발생해도 기본 번역 결과 사용
          // ⚠ translatedImgUrl 은 원본 URL 이다 — 글자가 번역되지 않은 이미지. process() 가 재시도·경고하도록 표시한다
          const processedImage = {
            index: index,
            url: translatedImgUrl,
            requestId: requestId,
            originalUrls: [imageUrl],
            processType: 'translated',
            translationData: transInfo,
            groupSize: 1,
            lastEdited: new Date(),
            isTranslated: translatedImgUrl !== imageUrl,
            translationFailed: true,
            failReason: `AI 번역 실패: ${aiError && aiError.message ? aiError.message : '알 수 없는 오류'}`
          };

          return processedImage;
        }
      } catch (ocrError) {
        console.error('OCR 데이터 조회 오류:', ocrError);
        logServiceError('detailImageService', 'translateSingleImage', ocrError, {
          errorType: ERROR_TYPES.IMAGE_PROCESS_FAILED,
          fallbackAction: '기본 번역 결과 사용',
          additional: { imageIndex: index, step: 'OCR조회' }
        });

        // OCR 오류 발생해도 기본 번역 결과 사용
        // ⚠ 원본(미번역) 이미지다 — process() 가 재시도·경고하도록 표시한다
        const processedImage = {
          index: index,
          url: translatedImgUrl,
          requestId: requestId,
          originalUrls: [imageUrl],
          processType: 'translated',
          translationData: transInfo,
          groupSize: 1,
          lastEdited: new Date(),
          isTranslated: translatedImgUrl !== imageUrl,
          translationFailed: true,
          failReason: `OCR 조회 실패: ${ocrError && ocrError.message ? ocrError.message : '알 수 없는 오류'}`
        };

        return processedImage;
      }
    } catch (error) {
      console.error('이미지 번역 오류:', error);
      throw error;
    }
  },

  /**
   * 영어 이미지 번역 처리 (영어→한국어) - 완전한 처리 과정 포함
   * @param {string} imageUrl - 번역할 이미지 URL
   * @param {number} index - 이미지 인덱스
   * @returns {Promise<Object>} - 번역 결과 객체
   */
  async translateEnglishImage(imageUrl, index) {
    if (!imageUrl) {
      console.error('[디버깅] translateEnglishImage: 이미지 URL이 없습니다');
      throw new Error('이미지 URL이 없습니다');
    }

    console.log(`[디버깅] translateEnglishImage 시작 - 인덱스: ${index}, URL: ${imageUrl.substring(0, 50)}...`);
    console.trace('[디버깅] translateEnglishImage 호출 스택 추적');

    try {
      console.log(`영어 이미지 번역 시작, 인덱스 ${index}:`, imageUrl.substring(0, 50) + '...');

      // 1. 영어→한국어 이미지 번역 API 호출
      const apiStartTime = Date.now();
      const res = await axios.post('/api/aiPage/getImageTranslateV2', {
        sourceLanguage: 'ENG',
        targetLanguage: 'KOR',
        imgUrl: imageUrl
      });
      const apiEndTime = Date.now();

      console.log(`영어 번역 API 응답 시간: ${apiEndTime - apiStartTime}ms`);

      if (res.data.요청결과 !== '성공' || !res.data.result || res.data.result.length === 0) {
        throw new Error('영어 이미지 번역 API 응답 실패: ' + (res.data.error || '알 수 없는 오류'));
      }

      // 번역 API 결과
      const transInfo = res.data.result[0];
      console.log('영어 번역 API 응답 데이터:', transInfo);
      console.log('영어 번역 API Data 구조:', transInfo.Data);

      // 번역 결과에서 RequestId 추출
      const requestId = transInfo.RequestId || transInfo.requestId || '';

      // 로깅을 위해 API에서 제공한 URL들 저장 - 모든 가능한 필드 확인
      const apiProvidedUrl = transInfo.Data?.SslUrl || transInfo.Data?.Url;
      const translatedUrl = transInfo.Data?.SslTranslateUrl ||
                           transInfo.Data?.TranslateUrl ||
                           transInfo.Data?.SslUrl ||  // 번역된 이미지가 SslUrl에 있을 수도 있음
                           transInfo.Data?.Url;       // 또는 Url에 있을 수도 있음

      console.log('영어 번역 API 제공 URL (로그용):', apiProvidedUrl ? apiProvidedUrl.substring(0, 50) + '...' : '없음');
      console.log('영어 번역된 이미지 URL:', translatedUrl ? translatedUrl.substring(0, 50) + '...' : '없음');
      console.log('Data 객체의 모든 키:', Object.keys(transInfo.Data || {}));

      // 번역된 이미지 URL 사용 (영어는 OCR 처리 없이 번역된 이미지만 사용)
      // API 응답에서 번역된 이미지는 보통 SslUrl 또는 Url에 있음
      const finalImageUrl = translatedUrl || apiProvidedUrl || imageUrl;

      // RequestId가 없고 번역된 이미지도 없는 경우에만 기본 처리
      if (!requestId && finalImageUrl === imageUrl) {
        console.warn('영어 번역: RequestId가 없고 번역된 이미지도 없어서 기본 처리합니다.');

        const processedImage = {
          index: index,
          url: finalImageUrl,
          requestId: requestId,
          originalUrls: [imageUrl],
          processType: 'translated',
          translationData: transInfo,
          groupSize: 1,
          lastEdited: new Date(),
          isTranslated: false,
          sourceLanguage: 'ENG',
          targetLanguage: 'KOR',
          // 원본(미번역) 이미지다 — 번역 결과를 못 받았다
          translationFailed: true,
          failReason: '번역 요청 번호·번역 이미지 없음'
        };

        return processedImage;
      }

      // 번역된 이미지가 있으면 계속 진행
      console.log(`영어 번역 진행: RequestId=${requestId}, 번역된 이미지=${finalImageUrl !== imageUrl ? '있음' : '없음'}`);
      console.log(`최종 이미지 URL: ${finalImageUrl.substring(0, 50)}...`);

      // 2. OCR 데이터 조회 (배치 쿼리) - 영어도 중국어와 동일한 과정 진행
      console.log(`영어 OCR 정보 조회 시작 - RequestId: ${requestId}`);

      try {
        // 배치 쿼리 API 호출
        const result = await this.getImageTranslateBatchQuery([requestId]);
        console.log('영어 배치 쿼리 결과:', result);

        if (!result || !result.Data || !result.Data.Content) {
          console.warn('영어 OCR 데이터 조회 결과가 없음, 기본 번역 이미지 사용');
          // OCR 없이 기본 번역 이미지 사용
          return await this.processEnglishImageBasic(index, imageUrl, finalImageUrl, requestId, transInfo);
        }

        // OCR 정보 (텍스트)
        const ocrInfo = result.Data.Content[requestId];
        if (!ocrInfo || !ocrInfo.Orc) {
          console.warn('영어 OCR 정보가 없음, 기본 번역 이미지 사용');
          // OCR 없이 기본 번역 이미지 사용
          return await this.processEnglishImageBasic(index, imageUrl, finalImageUrl, requestId, transInfo);
        }

        // 3. 텍스트 번역을 AI로 개선 - getAITranslate (영어→한국어)
        console.log('영어 OCR 추출 텍스트:', ocrInfo.Orc.map(e => e.source).join(', '));

        try {
          // AI 모델에 전송할 데이터 구성 (영어 소스)
          const englishSources = ocrInfo.Orc.map(e => e.source);

          // AI 번역 요청 (확장 통신으로 구현됨) - OCR 정보 전달
          const aiResult = await this.getAITranslate(imageUrl, englishSources, ocrInfo);
          console.log('영어 AI 번역 결과:', aiResult);

          if (aiResult && aiResult.length > 0) {
            // OCR 데이터에 AI 번역 결과 적용
            ocrInfo.AITranslate = aiResult;

            // OCR 데이터 가공 (영어→한국어 번역용)
            const ocrData = this.processEnglishOCRData(ocrInfo.Orc, aiResult);

            // 4. 번역된 텍스트로 최종 이미지 생성 - getImageOCR

            const ocrParams = {
              preRequestId: requestId,
              url: transInfo.Data?.Url || '',
              rmUrl: transInfo.Data?.RmUrl || '',
              ocrData: ocrData
            };
            console.log('[디버깅] 영어 OCR 파라미터:', ocrParams);

            // OCR API 호출 - 새 이미지 생성
            console.log('[디버깅] 영어 getImageOCR API 호출 시작');
            const transInfoOCR = await axios.post('/api/aiPage/getImageOCR', ocrParams);
            console.log('[디버깅] 영어 OCR 이미지 생성 결과:', transInfoOCR.data);

            if (transInfoOCR.data.요청결과 === '성공' && transInfoOCR.data.result) {
              // 최종 이미지 URL 및 정보 가져오기
              const finalRequestId = transInfoOCR.data.result.RequestId;
              const finalUrl = transInfoOCR.data.result.Data?.SslUrl ||
                transInfoOCR.data.result.Data?.Url ||
                finalImageUrl;

              console.log('[디버깅] 영어 OCR 생성 후 최종 URL 정보:', {
                finalRequestId: finalRequestId || 'none',
                finalUrl: finalUrl ? finalUrl.substring(0, 50) + '...' : 'none',
                isHttpUrl: finalUrl ? finalUrl.startsWith('http') : false
              });

              // URL 접근성 체크 및 S3 업로드 처리
              return await this.processEnglishImageWithS3Upload(index, imageUrl, finalUrl, finalRequestId || requestId, transInfoOCR.data.result, ocrInfo);
            } else {
              throw new Error('영어 OCR 이미지 생성 실패: ' + (transInfoOCR.data.error || '알 수 없는 오류'));
            }
          } else {
            // AI 개선 결과가 없는 경우 기본 번역 결과 사용
            console.warn('영어 AI 개선 결과가 없음, 기본 번역 이미지 사용');
            return await this.processEnglishImageBasic(index, imageUrl, finalImageUrl, requestId, transInfo, ocrInfo);
          }
        } catch (aiError) {
          console.error('영어 AI 번역 또는 OCR 생성 오류:', aiError);
          // AI 오류 발생해도 기본 번역 결과 사용
          return await this.processEnglishImageBasic(index, imageUrl, finalImageUrl, requestId, transInfo, ocrInfo);
        }
      } catch (ocrError) {
        console.error('영어 OCR 데이터 조회 오류:', ocrError);
        // OCR 오류 발생해도 기본 번역 결과 사용
        return await this.processEnglishImageBasic(index, imageUrl, finalImageUrl, requestId, transInfo);
      }

    } catch (error) {
      console.error('영어 이미지 번역 오류:', error);
      throw error;
    }
  },

  /**
   * 영어 OCR 데이터 처리 (AI 번역 결과 적용)
   */
  processEnglishOCRData(orcArray, aiResult) {
    return orcArray.map(e => {
      const obj = JSON.parse(JSON.stringify(e)); // 깊은 복사
      delete obj.id;

      // AI 번역 결과에서 일치하는 항목 찾기 (영어 소스 기준)
      const aiMatch = aiResult.find(f => f.en === e.source || f.ch === e.source);

      if (aiMatch) {
        // 번역된 텍스트 적용 (이모지 제거 필터 적용)
        let translatedText = aiMatch.ko[0] || e.target;
        obj.target = this.removeEmojis(translatedText);

        // 폰트 사이즈가 추천됐다면 적용
        if (aiMatch.font_size && typeof aiMatch.font_size === 'number') {
          obj.font_size = `${aiMatch.font_size}px`;
          console.log(`영어 폰트 사이즈 변경: "${e.source}" - ${e.font_size} → ${obj.font_size}`);
        }

        // 영어→한국어 텍스트 길이 기반 스마트 bounding box 조정
        this.adjustEnglishBoundingBox(obj, e, aiMatch);
      } else {
        // AI 매칭 결과가 없으면 원본 타겟 유지
        obj.target = e.target;
      }

      return obj;
    });
  },

  /**
   * 영어→한국어 bounding box 조정
   */
  adjustEnglishBoundingBox(obj, originalOcr, aiMatch) {
    try {
      if (!obj.bounding_box) return;

      const sourceLength = originalOcr.source.length;
      const targetLength = obj.target.length;
      const textLengthRatio = targetLength / sourceLength;

      let boxCoords = JSON.parse(obj.bounding_box.replace(/\s+/g, ''));
      if (!Array.isArray(boxCoords) || boxCoords.length < 4) return;

      // 기존 bounding box 크기 계산
      const x1 = boxCoords[0][0];
      const y1 = boxCoords[0][1];
      const x2 = boxCoords[1][0];
      const y3 = boxCoords[2][1];
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y3 - y1);
      const centerX = x1 + width / 2;
      const centerY = y1 + height / 2;

      const alignment = obj.align || 'left';
      const lineCount = parseInt(obj.line_count) || 1;

      let widthPercent = 100;
      let heightPercent = 100;

      // AI 제안 값 우선 사용
      if (aiMatch.adjust_box) {
        widthPercent = aiMatch.adjust_box.width_percent || 100;
        heightPercent = aiMatch.adjust_box.height_percent || 100;
      } else {
        // 영어→한국어 자동 조정
        if (textLengthRatio > 1.2) {
          widthPercent = Math.min(160, Math.round(textLengthRatio * 110));
          if (lineCount > 1) {
            heightPercent = Math.min(150, 100 + (widthPercent - 100) / 3);
          }
        } else if (textLengthRatio < 0.8) {
          widthPercent = Math.max(85, Math.round(textLengthRatio * 100));
        }

        const fontSize = parseInt(obj.font_size) || 0;
        if (fontSize >= 48 && widthPercent > 100) {
          widthPercent += 15;
        }
      }

      // 새 크기 계산
      const newWidth = width * (widthPercent / 100);
      const newHeight = height * (heightPercent / 100);

      // 정렬에 따른 좌표 계산
      let newX1, newX2;
      if (alignment === 'center') {
        newX1 = centerX - newWidth / 2;
        newX2 = centerX + newWidth / 2;
      } else if (alignment === 'right') {
        newX2 = x2;
        newX1 = newX2 - newWidth;
      } else {
        newX1 = x1;
        newX2 = newX1 + newWidth;
      }

      const newY1 = centerY - newHeight / 2;
      const newY3 = centerY + newHeight / 2;

      const newBoxCoords = [
        [Math.round(newX1), Math.round(newY1)],
        [Math.round(newX2), Math.round(newY1)],
        [Math.round(newX2), Math.round(newY3)],
        [Math.round(newX1), Math.round(newY3)]
      ];

      obj.bounding_box = JSON.stringify(newBoxCoords).replace(/\],\[/g, '], [');
      console.log(`영어 Bounding box 조정: "${originalOcr.source}" -> "${obj.target}" - 비율: ${textLengthRatio.toFixed(2)}, 너비: ${widthPercent}%, 높이: ${heightPercent}%`);
    } catch (error) {
      console.warn(`영어 Bounding box 조정 실패: "${originalOcr.source}" - ${error.message}`);
    }
  },

  /**
   * 영어 이미지 S3 업로드 처리
   */
  async processEnglishImageWithS3Upload(index, imageUrl, finalUrl, requestId, transResult, ocrInfo) {
    let needsS3Upload = false;
    let processedUrl = finalUrl;

    // URL 접근성 확인
    if (finalUrl && finalUrl.startsWith('http')) {
      try {
        console.log('[디버깅] 영어 번역 URL 접근성 확인 중...');
        const response = await fetch(finalUrl, {
          method: 'HEAD',
          cache: 'no-cache',
          timeout: 3000
        });
        const isAccessible = response.ok;
        console.log(`[디버깅] 영어 번역 URL 접근 결과: ${isAccessible ? '성공' : '실패'} (${response.status})`);
        needsS3Upload = !isAccessible;
      } catch (err) {
        console.log(`[디버깅] 영어 번역 URL 접근성 확인 오류: ${err.message}`);
        needsS3Upload = true;
      }
    } else {
      needsS3Upload = true;
    }

    // S3 업로드 처리
    if (needsS3Upload) {
      try {
        console.log('[디버깅] 영어 번역 이미지 S3 업로드 시작...');
        const uploadResult = await imageService.uploadImageToS3(finalUrl, `english-translated-${index}-${Date.now()}`, 'detail');

        if (uploadResult && uploadResult.url) {
          processedUrl = uploadResult.url;
          console.log('[디버깅] 영어 번역 이미지 S3 업로드 완료:', processedUrl.substring(0, 50) + '...');
        } else {
          console.warn('[디버깅] 영어 번역 이미지 S3 업로드 실패, 원본 URL 사용');
        }
      } catch (uploadError) {
        console.error('[디버깅] 영어 번역 이미지 S3 업로드 오류:', uploadError);
      }
    }

    // 최종 처리된 이미지 정보 반환
    const processedImage = {
      index: index,
      url: processedUrl,
      requestId: requestId,
      originalUrls: [imageUrl],
      processType: 'translated',
      translationData: transResult,
      ocrInfo: ocrInfo,
      groupSize: 1,
      lastEdited: new Date(),
      isTranslated: true,
      sourceLanguage: 'ENG',
      targetLanguage: 'KOR',
      isBase64: false
    };

    return processedImage;
  },

  /**
   * 영어 이미지 기본 처리 (OCR 없이)
   */
  async processEnglishImageBasic(index, imageUrl, finalImageUrl, requestId, transInfo, ocrInfo = null) {
    let needsS3Upload = false;
    let processedUrl = finalImageUrl;

    // URL 접근성 확인
    if (finalImageUrl && finalImageUrl.startsWith('http')) {
      try {
        const response = await fetch(finalImageUrl, {
          method: 'HEAD',
          cache: 'no-cache',
          timeout: 3000
        });
        needsS3Upload = !response.ok;
      } catch (err) {
        needsS3Upload = true;
      }
    } else {
      needsS3Upload = true;
    }

    // S3 업로드 처리
    if (needsS3Upload) {
      try {
        const uploadResult = await imageService.uploadImageToS3(finalImageUrl, `english-basic-${index}-${Date.now()}`, 'detail');
        if (uploadResult && uploadResult.url) {
          processedUrl = uploadResult.url;
        }
      } catch (uploadError) {
        console.error('영어 기본 이미지 S3 업로드 오류:', uploadError);
      }
    }

    const processedImage = {
      index: index,
      url: processedUrl,
      requestId: requestId,
      originalUrls: [imageUrl],
      processType: 'translated',
      translationData: transInfo,
      ocrInfo: ocrInfo,
      groupSize: 1,
      lastEdited: new Date(),
      isTranslated: processedUrl !== imageUrl,
      sourceLanguage: 'ENG',
      targetLanguage: 'KOR',
      isBase64: false
    };
    // 번역 이미지 URL 을 못 받아 원본을 쓴 경우(S3 로 옮겼어도 내용은 원본) — 실패로 표시한다
    if (finalImageUrl === imageUrl) {
      processedImage.translationFailed = true;
      processedImage.failReason = '번역 이미지 없음';
    }

    return processedImage;
  },

  /**
   * OCR 정보 조회 함수
   * @param {Array} requestIds - RequestId 배열
   * @returns {Promise<Object>} - OCR 정보 결과
   */
  async getImageTranslateBatchQuery(requestIds) {
    return new Promise((resolve, reject) => {
      try {
        axios.post('/api/aiPage/getImageTranslateBatchQuery', requestIds)
          .then(response => {
            if (response.data.요청결과 === '성공') {
              resolve(response.data.result);
            } else {
              reject(new Error(response.data.error || '배치 쿼리 실패'));
            }
          })
          .catch(error => {
            console.error('배치 쿼리 에러:', error);
            reject(error);
          });
      } catch (error) {
        console.error('배치 쿼리 예외:', error);
        reject(error);
      }
    });
  },

  /**
   * 중국어 텍스트 번역을 위한 프롬프트 생성
   * @param {string} imgUrl - 이미지 URL
   * @param {Array} chArr - 중국어 텍스트 배열
   * @param {Object} ocrInfo - OCR 정보 객체 (Orc 필드 포함)
   * @returns {string} 생성된 프롬프트
   */
  createPromptForTranslation(imgUrl, chArr, ocrInfo) {
    // OCR 데이터에서 1차 번역된 한국어 추출 및 폰트 정보 수집
    let translations = {};
    let fontSizes = {};

    // ocrInfo가 있고 Orc 배열이 있는 경우 1차 번역 데이터 구성
    if (ocrInfo && ocrInfo.Orc && Array.isArray(ocrInfo.Orc)) {
      // OCR 정보에서 소스 텍스트, 타겟 번역, 폰트 크기 매핑
      ocrInfo.Orc.forEach(item => {
        if (item.source && item.target) {
          translations[item.source] = item.target;

          // 폰트 크기 정보 추출 (예: "35px" -> 35)
          if (item.font_size) {
            const sizeMatch = item.font_size.match(/(\d+)/);
            fontSizes[item.source] = sizeMatch ? parseInt(sizeMatch[1]) : 30;
          }
        }
      });
      console.log('OCR 1차 번역 및 폰트 매핑 생성:', translations, fontSizes);
    } else {
      console.warn('OCR 정보가 없거나 유효하지 않음');
      // 기본값을 사용하지 않음 - 배치 쿼리 결과가 없으면 번역 진행하지 않음
      return null;
    }

    // OCR 데이터에서 bounding box 정보 추출
    let boundingBoxes = {};
    let alignments = {};
    let lineCountInfo = {};

    if (ocrInfo && ocrInfo.Orc && Array.isArray(ocrInfo.Orc)) {
      ocrInfo.Orc.forEach(item => {
        if (item.source) {
          // bounding box 정보 저장
          if (item.bounding_box) {
            boundingBoxes[item.source] = item.bounding_box;
          }

          // 정렬 정보 저장
          if (item.align) {
            alignments[item.source] = item.align;
          }

          // 줄 수 정보 저장
          if (item.line_count) {
            lineCountInfo[item.source] = item.line_count;
          }
        }
      });
    }

    // 중국어 텍스트와 1차 번역 목록을 포맷팅 (폰트 정보, bounding box 정보 포함)
    const textSources = chArr.map((text, index) => {
      // 번역이 있으면 사용, 없으면 '번역 필요' 표시
      const koreanTrans = translations[text] || `번역 필요 (${text})`;
      const charCount = koreanTrans.length;
      const fontSize = fontSizes[text] || "unknown";
      const boundingBox = boundingBoxes[text] || "unknown";
      const alignment = alignments[text] || "left";
      const lineCount = lineCountInfo[text] || 1;

      // bounding box에서 너비와 높이 계산 (있는 경우)
      let boxWidth = "unknown";
      let boxHeight = "unknown";

      if (boundingBox !== "unknown") {
        try {
          const coordinates = JSON.parse(boundingBox.replace(/\s+/g, ''));
          if (Array.isArray(coordinates) && coordinates.length >= 4) {
            // [top-left, top-right, bottom-right, bottom-left] 형식 가정
            const x1 = coordinates[0][0];
            const x2 = coordinates[1][0];
            const y1 = coordinates[0][1];
            const y3 = coordinates[3][1];

            boxWidth = Math.abs(x2 - x1);
            boxHeight = Math.abs(y3 - y1);
          }
        } catch (e) {
          console.warn(`bounding box 파싱 실패: ${boundingBox}`);
        }
      }

      return `${index + 1}. Chinese: "${text}" / First Translation: "${koreanTrans}"
   - Current font size: ${fontSize}px
   - Character count: ${charCount} chars
   - Bounding box: ${boundingBox}
   - Box dimensions: Width=${boxWidth}px, Height=${boxHeight}px
   - Text alignment: ${alignment}
   - Line count: ${lineCount}`;
    }).join('\n\n');

    // 번역된 원본 이미지 URL 가져오기 (있는 경우)
    let translatedImageUrl = "";
    if (ocrInfo && ocrInfo.Data && (ocrInfo.Data.SslUrl || ocrInfo.Data.Url)) {
      translatedImageUrl = ocrInfo.Data.SslUrl || ocrInfo.Data.Url;
    }

    // 사용자 설정에서 번역 프롬프트 스타일 가져오기
    let customPromptStyle = '';
    if (store.state.settings && store.state.settings[0] &&
        store.state.settings[0].defaultSettings &&
        store.state.settings[0].defaultSettings.imageSettings &&
        store.state.settings[0].defaultSettings.imageSettings.translationPromptStyle) {
      customPromptStyle = store.state.settings[0].defaultSettings.imageSettings.translationPromptStyle;
    }

    // 기본 프롬프트 스타일 (사용자 설정이 없을 때 사용)
    // 원문에 충실한 번역 — 광고 문구로 부풀리면 소재·인증·수치가 원문보다 세진다(PR261016-11F5C4 «检测→인증», «최상급»)
    const defaultPromptStyle = `- Translate faithfully into natural, easy-to-read Korean for online shoppers
- DO NOT use emoticons or emojis in any translations
- Keep the meaning of each line; only shorten wording when the text would not fit its box
- For product specs, keep every value exactly as written (material, size, number, unit)
- Use short words and abbreviations when possible`;

    const creativeDirection = customPromptStyle || defaultPromptStyle;

    return `# Role: Product Image Translator & Layout Design Expert

## Background
You are both a careful e-commerce translator and a visual layout design specialist for product images. Your job is to translate each text element into faithful, natural Korean AND recommend consistent font sizes for a harmonious visual layout that respects the original image's design aesthetics.

## Task
1. FIRST analyze the original image to understand its design style and visual hierarchy
2. Translate each text element into faithful, natural Korean
3. Recommend standardized font sizes that maintain the visual harmony of the ORIGINAL DESIGN

Original image URL: ${imgUrl}
${translatedImageUrl ? `First-translated image URL: ${translatedImageUrl}` : ''}

Text elements (with current font information):
${textSources}

## RULES (In Order of Priority)
0. FACTUAL FIDELITY (overrides every other rule and the Creative Direction) - Translate claims exactly as strong as the Chinese source, never stronger:
   - Certification: 检测/检验/质检/验货 = "검사"/"검수". Write "인증" ONLY when the source says 认证/证书/CE/KC etc.
   - Material: translate the material word literally (棉麻 = 면·마, 纳帕皮 = 나파 가죽). Never upgrade or change a material
   - Numbers, units, percentages, sizes: copy exactly
   - No superlatives (최고, 최고급, 최상급, 최강, No.1, 1위) unless the source has 最/顶级/第一
   - Do not add benefits, effects or claims that are not in the source text
1. RESPECT ORIGINAL DESIGN - Maintain the visual hierarchy and aesthetic balance of the original image
2. MEANING - Keep each line's meaning; natural Korean that reads well to Korean shoppers
3. LAYOUT CONSISTENCY - Standardize font sizes while preserving the original design intent
4. TEXT FIT & BOUNDING BOX - Recommend bounding box adjustments based on text length changes
5. NATURAL FLOW - Don't force awkward phrasing just to match character count exactly

## Bounding Box Adjustment Guidelines
- ANALYZE how your Korean text length compares to the original Chinese text length
- RECOMMEND bounding box width/height adjustments as percentages:
  - WIDER BOX (e.g., 110%, 120%): If Korean text is longer than Chinese and may not fit
  - TALLER BOX (e.g., 110%, 120%): If line breaks are needed or text is significantly taller
  - NO CHANGE (100%): If text fits well in the current box dimensions
  - NARROWER BOX (e.g., 90%, 80%): If text is much shorter and box looks too empty
- RESPECT LAYOUT CONSTRAINTS: Don't suggest adjustments that would cause text to overlap with other elements

## Font Size Guidelines
- ANALYZE the original image's typography patterns and hierarchies first
- STANDARDIZE font sizes into 2-3 consistent size groups while maintaining the original design's intent:
  - Headings/Main text (제목/주요문구): 48-52px
  - Subheadings (부제목): 36-42px
  - Body text (본문): 24-30px

## CRITICAL DESIGN CONSISTENCY RULES (MUST FOLLOW)
- STRUCTURALLY RELATED ELEMENTS MUST HAVE IDENTICAL FONT SIZES:
  - Left/right symmetrical elements → EXACTLY SAME font size
  - Items in the same list/bullet points → EXACTLY SAME font size
  - Section titles/subtitles at the same level → EXACTLY SAME font size
  - Paired elements (before/after, top/bottom) → EXACTLY SAME font size
- GUARANTEE VISUAL BALANCE - Check if texts appear in a grid, table, or symmetrical layout
- IDENTIFY PATTERN GROUPS - Give identical font sizes to texts that serve the same function
- Preserve original design's emphasis patterns (what was BIG should remain BIG)
- MAINTAIN CONSISTENCY WITHIN FONT SIZE GROUPS - If multiple texts in the original image share the same font size, ensure their Korean translations ALSO share the same font size

## TEXT LENGTH MANAGEMENT
- CRITICAL: For texts that must fit within specific layout constraints:
  - Carefully compare the first-translated Korean text length with the desired layout space
  - If the translated Korean text is too long for its position, MODIFY content to fit by:
    1. Using more concise wording while preserving key meaning
    2. Using abbreviations where appropriate
    3. Simplifying expressions without losing impact
  - Aim for text that fits naturally without appearing crammed or causing layout issues
  - Reference the first-translated image to see how current text length affects layout

## Creative Direction
${creativeDirection}

## Output Format (JSON array)
[
  {
    "ch": "Chinese original text",
    "ko": ["Korean marketing copy (optimal for impact and naturalness)"],
    "font_size": 40,  // Recommended font size as an integer (no "px")
    "adjust_box": {
      "width_percent": 100,  // Keep original width (100%), or suggest adjustment (e.g., 110% for wider)
      "height_percent": 100  // Keep original height (100%), or suggest adjustment (e.g., 120% for taller)
    }
  },
  ...
]

## FINAL VERIFICATION
1. STRUCTURAL CONSISTENCY - VERIFY that all structurally related elements have IDENTICAL font sizes
2. VISUAL HARMONY - Confirm that your recommended font sizes create a logical visual hierarchy
3. PATTERN RECOGNITION - Check that similar elements follow consistent patterns (lists, sections, pairs)
4. STANDARDIZATION - Verify you've reduced random font sizes to 2-3 consistent size groups
5. COMMON SIZE GROUPING - Ensure texts that had the same font size in the original also have the same font size in your translation
6. BOUNDING BOX ADJUSTMENTS - Ensure your box adjustment recommendations are appropriate for text length
7. SPACE FITTING - Confirm that texts have been adjusted to fit their designated spaces while preserving core meaning
8. NATURAL FLOW - Ensure each text sounds natural and appealing to Korean shoppers
9. FACTUAL FIDELITY - Verify no certification, material, number or superlative is stronger than the Chinese source
10. NO EMOJIS OR EMOTICONS - Ensure all translations are free of emojis, emoticons, and special characters

Note: Your PRIMARY GOAL is to create a VISUALLY CONSISTENT LAYOUT where structurally related elements have identical font sizes and appropriate bounding boxes, eliminating any random or mismatched appearance.`;
  },

  /**
   * 확장 프로그램에 번역 요청 전송 - workflowService.requestExtensionWithPromise 사용
   * @param {Object} requestData - 요청 데이터
   * @returns {Promise<Object>} AI 번역 결과
   */
  async requestExtensionTranslate(requestData) {
    try {
      console.log('AI 번역 확장 요청 시작:', requestData);

      // workflowService.requestExtensionWithPromise 사용
      const result = await workflowService.requestExtensionWithPromise(
        {
          workType: 'AI_TRANSLATE',
          data: {
            imgUrl: requestData.imgUrl,
            chArr: requestData.chArr,
            prompt: requestData.prompt
          }
        },
        {
          timeout: 60000, // 60초 타임아웃
          maxRetries: 2   // 최대 2회 재시도
        }
      );

      console.log('AI 번역 확장 응답 수신:', result);
      return result;
    } catch (error) {
      console.error('AI 번역 확장 요청 실패:', error);
      throw error;
    }
  },

  /**
   * AI 번역 함수 - 확장 프로그램을 통해 AI 번역 요청
   * @param {string} imgUrl - 이미지 URL
   * @param {Array} chArr - 중국어 텍스트 배열
   * @param {Object} ocrInfo - OCR 정보 객체 (translateSingleImage에서 전달받음)
   * @returns {Promise<Array>} - AI 번역 결과
   */
  async getAITranslate(imgUrl, chArr, ocrInfo) {
    try {
      console.log('확장 프로그램에 AI 번역 요청 시작:', {
        이미지URL: imgUrl.substring(0, 30) + '...',
        중국어텍스트수: chArr.length,
        OCR정보있음: !!ocrInfo
      });

      // 번역 프롬프트 생성 (OCR 정보 전달)
      const prompt = this.createPromptForTranslation(imgUrl, chArr, ocrInfo);

      // OCR 데이터에서 1차 번역 및 폰트 크기 정보 추출
      let translations = {};
      let fontSizes = {};

      // OCR 정보에서 번역 데이터와 폰트 크기 데이터 추출
      if (ocrInfo && ocrInfo.Orc && Array.isArray(ocrInfo.Orc)) {
        ocrInfo.Orc.forEach(item => {
          if (item.source && item.target) {
            translations[item.source] = item.target;

            // 폰트 크기 정보 추출 (예: "35px" -> 35)
            if (item.font_size) {
              const sizeMatch = item.font_size.match(/(\d+)/);
              fontSizes[item.source] = sizeMatch ? parseInt(sizeMatch[1]) : 30;
            }
          }
        });
        console.log('타임아웃 대비 번역 및 폰트 정보 준비:', {
          번역개수: Object.keys(translations).length,
          폰트개수: Object.keys(fontSizes).length
        });
      }

      // 요청 데이터 구성
      const requestData = {
        imgUrl: imgUrl,
        chArr: chArr,
        prompt: prompt
      };

      try {
        // 확장 프로그램에 요청 전송하고 결과 기다리기
        const result = await this.requestExtensionTranslate(requestData);
        console.log('AI 번역 결과 수신:', result);

        // 결과 파싱
        const translationResult = this.parseMeasurementInfo(result);
        if (translationResult !== null) {
          return translationResult;
        } else {
          // 파싱 실패 시 1차 번역 결과 사용
          console.warn('AI 번역 결과 파싱 실패 - 1차 번역 결과를 대신 사용합니다:', result);
          // 1차 번역도 하나도 없으면 중국어를 그대로 다시 그리게 된다 → 실패로 올려 호출부가 translationFailed 를 붙이게 한다
          if (hasNoFallbackTranslation(chArr, translations)) {
            throw new Error('AI 번역 결과 파싱 실패, 1차 번역도 없음');
          }
          const fallbackResult = chArr.map(ch => ({
            ch: ch,
            ko: [translations[ch] || ch], // 1차 번역이 있으면 사용, 없으면 원본 그대로
            font_size: fontSizes[ch] || 40 // 기존 폰트 사이즈 사용, 없으면 기본값 40
          }));
          return fallbackResult;
        }
      } catch (error) {
        console.error('AI 번역 요청 실패 - 1차 번역 결과를 대신 사용합니다:', error);
        // 1차 번역도 하나도 없으면 중국어를 그대로 다시 그리게 된다 → 실패로 올려 호출부가 translationFailed 를 붙이게 한다
        if (hasNoFallbackTranslation(chArr, translations)) {
          throw new Error(`AI 번역 실패, 1차 번역도 없음: ${error && error.message ? error.message : '알 수 없는 오류'}`);
        }
        const fallbackResult = chArr.map(ch => ({
          ch: ch,
          ko: [translations[ch] || ch], // 1차 번역이 있으면 사용, 없으면 원본 그대로
          font_size: fontSizes[ch] || 40 // 기존 폰트 사이즈 사용, 없으면 기본값 40
        }));
        return fallbackResult;
      }
    } catch (error) {
      console.error('AI 번역 요청 설정 중 오류:', error);
      throw error;
    }
  },

  /**
   * 이모티콘/이모지 제거 유틸리티 함수
   * @param {string} text - 필터링할 텍스트
   * @returns {string} - 이모지가 제거된 텍스트
   */
  removeEmojis(text) {
    if (!text) return text;

    // 이모지 문자 범위를 포함하는 정규식
    return text.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '');
  },

  /**
   * JSON 문자열 파싱 유틸리티 - Gemini API 응답 처리를 위해 개선
   * @param {string|Object} rawString - JSON 문자열 또는 이미 파싱된 객체
   * @returns {Array|null} - 파싱된 JSON 객체 또는 null
   */
  parseMeasurementInfo(rawString) {
    if (!rawString) return null;

    // 이미 객체인 경우 (백그라운드에서 파싱된 경우)
    if (typeof rawString === 'object') {
      console.log('[DEBUG] 객체 파싱 시작:', rawString);

      // 다양한 중첩 객체 구조 처리
      if (Array.isArray(rawString)) {
        console.log('[DEBUG] 배열 발견, 이모지 필터링 후 반환');
        // 배열 내 모든 항목에서 이모지 제거
        return rawString.map(item => {
          if (item.ko && Array.isArray(item.ko)) {
            item.ko = item.ko.map(text => this.removeEmojis(text));
          }
          return item;
        });
      }

      // translateResult.batchResults 구조 확인 (새로운 응답 형식)
      if (rawString.translateResult && rawString.translateResult.batchResults && rawString.translateResult.batchResults.length > 0) {
        console.log('[DEBUG] translateResult.batchResults 구조 발견');
        const firstResult = rawString.translateResult.batchResults[0];

        if (firstResult.result && Array.isArray(firstResult.result)) {
          console.log('[DEBUG] translateResult.batchResults[0].result 배열 반환 (이모지 필터링 적용)');
          // 결과 배열에서 이모지 제거
          return firstResult.result.map(item => {
            if (item.ko && Array.isArray(item.ko)) {
              item.ko = item.ko.map(text => this.removeEmojis(text));
            }
            return item;
          });
        }

        console.log('[DEBUG] translateResult.batchResults는 있지만 내부 result 배열 없음');
      }

      // processAIRocketRequest 응답 구조 (batchResults)
      if (rawString.batchResults && rawString.batchResults.length > 0) {
        console.log('[DEBUG] batchResults 구조 발견');
        const firstResult = rawString.batchResults[0];

        if (firstResult.result && Array.isArray(firstResult.result)) {
          console.log('[DEBUG] batchResults[0].result 배열 반환 (이모지 필터링 적용)');
          // 결과 배열에서 이모지 제거
          return firstResult.result.map(item => {
            if (item.ko && Array.isArray(item.ko)) {
              item.ko = item.ko.map(text => this.removeEmojis(text));
            }
            return item;
          });
        }

        console.log('[DEBUG] batchResults는 있지만 내부 result 배열 없음');
      }

      // 직접 result 필드가 있는 경우
      if (rawString.result) {
        console.log('[DEBUG] 직접 result 필드 발견');
        const result = Array.isArray(rawString.result) ? rawString.result : null;
        if (result) {
          console.log('[DEBUG] result 배열 반환 (이모지 필터링 적용)');
          // 결과 배열에서 이모지 제거
          return result.map(item => {
            if (item.ko && Array.isArray(item.ko)) {
              item.ko = item.ko.map(text => this.removeEmojis(text));
            }
            return item;
          });
        }
      }

      // 지원되지 않는 객체 형식이지만 정보는 로깅
      console.warn('[DEBUG] 지원되지 않는 객체 형식 - 자세한 정보 로깅:', JSON.stringify(rawString).substring(0, 200));

      // translateResult.batchResults에서 processedText 확인
      if (rawString.translateResult && rawString.translateResult.batchResults && Array.isArray(rawString.translateResult.batchResults) && rawString.translateResult.batchResults.length > 0) {
        const firstBatch = rawString.translateResult.batchResults[0];
        if (firstBatch.processedText) {
          console.log('[DEBUG] translateResult.batchResults에서 processedText 발견, 파싱 시도');
          try {
            // ```json 형식의 응답이면 파싱 시도
            const jsonStart = firstBatch.processedText.indexOf('```json');
            if (jsonStart !== -1) {
              const jsonEnd = firstBatch.processedText.lastIndexOf('```');
              if (jsonEnd !== -1 && jsonEnd > jsonStart) {
                const jsonText = firstBatch.processedText.substring(jsonStart + 7, jsonEnd).trim();
                const parsedData = JSON.parse(jsonText);
                if (Array.isArray(parsedData)) {
                  console.log('[DEBUG] translateResult.batchResults.processedText에서 유효한 JSON 배열 추출 성공');
                  // 이모지 필터링 적용
                  return parsedData.map(item => {
                    if (item.ko && Array.isArray(item.ko)) {
                      item.ko = item.ko.map(text => this.removeEmojis(text));
                    }
                    return item;
                  });
                }
              }
            }
          } catch (e) {
            console.warn('[DEBUG] translateResult.batchResults.processedText 파싱 시도 실패:', e.message);
          }
        }
      }

      // batchResults가 있는지 확인 - 부분적인 정보라도 활용 시도
      if (rawString.batchResults && Array.isArray(rawString.batchResults) && rawString.batchResults.length > 0) {
        // processedText를 추출하여 사용
        const firstBatch = rawString.batchResults[0];
        if (firstBatch.processedText) {
          console.log('[DEBUG] batchResults에서 processedText 발견, 파싱 시도');
          try {
            // ```json 형식의 응답이면 파싱 시도
            const jsonStart = firstBatch.processedText.indexOf('```json');
            if (jsonStart !== -1) {
              const jsonEnd = firstBatch.processedText.lastIndexOf('```');
              if (jsonEnd !== -1 && jsonEnd > jsonStart) {
                const jsonText = firstBatch.processedText.substring(jsonStart + 7, jsonEnd).trim();
                const parsedData = JSON.parse(jsonText);
                if (Array.isArray(parsedData)) {
                  console.log('[DEBUG] batchResults.processedText에서 유효한 JSON 배열 추출 성공');
                  // 이모지 필터링 적용
                  return parsedData.map(item => {
                    if (item.ko && Array.isArray(item.ko)) {
                      item.ko = item.ko.map(text => this.removeEmojis(text));
                    }
                    return item;
                  });
                }
              }
            }
          } catch (e) {
            console.warn('[DEBUG] batchResults.processedText 파싱 시도 실패:', e.message);
          }
        }
      }

      // 이 경우에도 null을 반환하지만, 호출 측에서는 1차 번역 결과로 대체될 것임
      return null;
    }

    try {
      // Gemini API가 반환하는 마크다운 코드 블록 처리
      const jsonStart = rawString.indexOf('```json');
      const jsonEnd = rawString.lastIndexOf('```');

      let cleanedText = rawString;
      // 마크다운 코드 블록 형식 제거
      if (jsonStart !== -1 && jsonEnd !== -1) {
        cleanedText = rawString.substring(jsonStart + 7, jsonEnd).trim();
        console.log('마크다운 코드 블록에서 추출된 JSON:', cleanedText);
      } else {
        // 작은따옴표를 큰따옴표로 교체 (JSON 형식에 맞게)
        cleanedText = rawString.replace(/'/g, '"');

        // 정규식으로 JSON 배열 부분 추출
        const jsonMatch = cleanedText.match(/(\[[\s\S]*\])/);
        if (jsonMatch && jsonMatch[1]) {
          cleanedText = jsonMatch[1];
          console.log('정규식으로 추출된 JSON:', cleanedText);
        }
      }

      // JSON 파싱 후 이모지 필터링 적용
      const parsedResult = JSON.parse(cleanedText);
      if (Array.isArray(parsedResult)) {
        return parsedResult.map(item => {
          if (item.ko && Array.isArray(item.ko)) {
            item.ko = item.ko.map(text => this.removeEmojis(text));
          }
          return item;
        });
      }
      return parsedResult;
    } catch (error) {
      console.error("JSON 문자열 파싱 오류:", error);
      console.error("원본 문자열:", rawString);
      return null;
    }
  },

  /**
   * 이미 처리된 이미지 재번역 - 원래 위치 유지
   * @param {Object} group - 이미지 그룹 정보
   * @param {Object} [claimBasis] - 소재 대조 근거(claimBasisOf(product)) — 일괄 처리와 같이 정정한다
   * @returns {Promise<Object>} - 번역 결과
   */
  async retranslateImage(group, claimBasis) {
    if (!group) {
      console.error('retranslateImage: 이미지 그룹이 유효하지 않음');
      throw new Error('이미지 그룹이 유효하지 않습니다');
    }

    // 원본 이미지 URL이 있으면 첫 번째 이미지 재번역
    if (group.originalUrls && group.originalUrls.length > 0) {
      const originalUrl = group.originalUrls[0];
      const originalIndex = group.index;

      if (!originalUrl) {
        console.error('retranslateImage: 원본 URL이 유효하지 않음');
        throw new Error('원본 이미지 URL이 유효하지 않습니다');
      }

      console.log('이미지 재번역 시작:', {
        그룹인덱스: group.index,
        원본인덱스: originalIndex,
        원본URL: originalUrl.substring(0, 30) + '...'
      });

      // 이미지 번역 수행
      const translatedImage = await this.translateSingleImage(originalUrl, originalIndex, claimBasis);

      // 중요: 원래 위치 정보를 유지
      translatedImage.index = group.index;

      return translatedImage;
    } else {
      // 원본 URL이 없는 경우 현재 URL로 재번역 시도
      if (group.url) {
        console.log('원본 이미지 없음, 현재 이미지로 재번역 시도:', group.url.substring(0, 30) + '...');
        const translatedImage = await this.translateSingleImage(group.url, group.index, claimBasis);

        // 중요: 원래 위치 정보를 유지
        translatedImage.index = group.index;

        return translatedImage;
      } else {
        throw new Error('재번역할 이미지 URL이 없습니다');
      }
    }
  },

  /**
 * 상세 이미지 처리 메인 함수 - 워크플로우 서비스 및 일괄 처리용
 * @param {Object} product - 상품 객체
 * @returns {Promise<Object>} 처리 결과
 */
  async processDetailImages(product) {
    console.log('[디버깅] processDetailImages 시작 - 상품 ID:', product.registrationId);
    console.trace('[디버깅] processDetailImages 호출 스택 추적');

    try {
      // 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '상세 이미지 처리 중...'
      });

      // 상세 이미지 배열 가져오기
      const detailImages = product.detailImages || [];

      // 이미지 URL 로깅 (문제 진단용)
      console.log(`상세 이미지 개수: ${detailImages.length}`);
      if (detailImages.length > 0) {
        console.log('첫 번째 이미지 URL:', detailImages[0].substring(0, 50) + '...');
        if (detailImages.length > 1) {
          console.log('마지막 이미지 URL:', detailImages[detailImages.length-1].substring(0, 50) + '...');
        }
      }

      if (detailImages.length === 0) {
        console.warn("상세 이미지가 없습니다.");
        return {
          status: 'completed',
          type: 'detailImageStatus',
          processedImages: []
        };
      }

      // 처리된 이미지를 저장할 배열
      const processedImages = [];

      // 처리 시작 시간 기록
      const startTime = Date.now();

      // 상단/하단 이미지 URL 가져오기 (설정에서)
      const settings = store.state.settings &&
                      store.state.settings[0] &&
                      store.state.settings[0].defaultSettings &&
                      store.state.settings[0].defaultSettings.imageSettings || {};

      const topImageUrl = settings.useTopImage && settings.topImageUrl ? settings.topImageUrl : null;
      const bottomImageUrl = settings.useBottomImage && settings.bottomImageUrl ? settings.bottomImageUrl : null;

      // 1. 이미지 번역 처리 - 각 이미지를 원본 URL로 처리
      // 오류 발생 시 건너뛰지 않고 계속 진행 (다른 이미지도 처리)
      for (let i = 0; i < detailImages.length; i++) {
        const imageUrl = detailImages[i];

        // 상단/하단 이미지인지 확인
        const isTopOrBottomImage = (() => {
          // 1. URL 직접 비교 (기존 방식)
          if (imageUrl === topImageUrl || imageUrl === bottomImageUrl) {
            return true;
          }

          // 2. 파일명에 topimage 또는 bottomimage 포함 여부 확인 (추가 방식)
          try {
            // URL에서 파일명 추출
            const urlObj = new URL(imageUrl);
            const pathname = urlObj.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

            // 파일명에 topimage 또는 bottomimage 포함 여부 확인
            return filename.includes('topimage') || filename.includes('bottomimage');
          } catch (error) {
            // URL 파싱 오류 시 기존 방식 결과 반환
            return false;
          }
        })();

        // 상단/하단 이미지인 경우 처리 건너뛰기
        if (isTopOrBottomImage) {
          console.log(`이미지 ${i + 1}/${detailImages.length}는 상단/하단 이미지이므로 AI 처리 건너뜀:`, imageUrl.substring(0, 50) + '...');

          // 원본 이미지 정보만 추가 (처리하지 않음)
          const fallbackImage = {
            index: i,
            url: imageUrl,  // 원본 URL 그대로 사용
            originalUrl: imageUrl,
            name: `${store.state.user.suppliercode || store.state.user.companyid}_${product.registrationId}_detail_${i}.jpg`,
            processType: 'original',
            isBase64: false,
            isSettingImage: true // 설정 이미지임을 표시
          };

          processedImages.push(fallbackImage);
          continue;
        }

        // 진행 상태 업데이트
        store.dispatch('setProcessing', {
          isProcessing: true,
          message: `상세 이미지 처리 중... (${i+1}/${detailImages.length})`
        });

        console.log(`이미지 ${i+1}/${detailImages.length} 처리 시작: ${imageUrl.substring(0, 50)}...`);

        // 각 이미지별 처리 시간 기록
        const imageStartTime = Date.now();

        try {
          // processImage 메서드는 항상 원본 이미지 URL을 사용하도록 수정됨
          // (내부적으로 항상 imageUrl을 사용하도록 변경됨)
          const processedImage = await this.processImage(imageUrl, product.registrationId);

          // 이미지 처리 시간 기록
          const imageProcessTime = Date.now() - imageStartTime;
          console.log(`이미지 ${i+1} 처리 시간: ${imageProcessTime}ms`);

          if (processedImage) {
            // 인덱스 정보 추가
            processedImage.index = i;
            processedImages.push(processedImage);
            console.log(`이미지 ${i+1} 처리 완료, URL: ${processedImage.url.substring(0, 30)}...`);
          } else {
            console.warn(`이미지 ${i+1} 처리 실패: 결과가 없음`);

            // 최소한의 기본 정보를 가진 객체 추가 (원본 이미지 유지)
            const fallbackImage = {
              index: i,
              url: imageUrl,  // 원본 URL 그대로 사용
              originalUrl: imageUrl,
              name: `${store.state.user.suppliercode || store.state.user.companyid}_${product.registrationId}_detail_${i}.jpg`,
              processType: 'original',
              isBase64: false
            };

            processedImages.push(fallbackImage);
            console.log(`이미지 ${i+1} 처리 실패 시 원본 URL 사용: ${imageUrl.substring(0, 30)}...`);
          }
        } catch (error) {
          console.error(`이미지 ${i+1} 처리 중 오류:`, error);

          // 오류가 발생해도 기본 정보 추가 (건너뛰지 않음)
          const fallbackImage = {
            index: i,
            url: imageUrl,  // 원본 URL 그대로 사용
            originalUrl: imageUrl,
            name: `${store.state.user.suppliercode || store.state.user.companyid}_${product.registrationId}_detail_${i}.jpg`,
            processType: 'error',
            error: error.message,
            isBase64: false
          };

          processedImages.push(fallbackImage);
          console.log(`이미지 ${i+1} 오류 발생 시 원본 URL 사용: ${imageUrl.substring(0, 30)}...`);
        }
      }

      // 전체 처리 완료 시간 기록
      const totalTime = Date.now() - startTime;
      console.log(`전체 이미지 처리 완료 - 총 ${processedImages.length}개, 처리 시간: ${totalTime}ms`);

      // 제품 정보 업데이트를 위한 데이터 반환
      return {
        status: 'completed',
        type: 'detailImageStatus',
        processedImages: processedImages // 처리된 이미지 정보 배열 (base64 데이터 포함)
      };
    } catch (error) {
      console.error('상세 이미지 처리 중 전체 오류:', error);
      return {
        status: 'error',
        type: 'detailImageStatus',
        detailImageStatus: 'error', // 문자열 형식으로 추가
        error: error.message
      };
    } finally {
      // 로딩 상태 해제
      store.dispatch('setProcessing', {
        isProcessing: false,
        message: ''
      });
    }
  },

  /**
   * 단일 이미지 처리 - S3 업로드 제거하고 base64 데이터 반환
   * @param {string} imageUrl - 처리할 이미지 URL
   * @param {string} productId - 상품 등록번호
   * @returns {Promise<Object>} 처리된 이미지 정보
   */
  async processImage(imageUrl, productId) {
    try {
      console.log(`[디버깅] processImage 시작 - 원본 이미지 URL: ${imageUrl.substring(0, 50)}...`);
      console.trace('[디버깅] processImage 호출 스택 추적');

      // 이미지 텍스트 번역 API 호출
      const result = await this.translateImage(imageUrl);

      // 번역 결과 처리
      if (!result || !result.translatedImagePixelData) {
        console.warn("[디버깅] 이미지 번역 결과가 없습니다.");
        return null;
      }

      // 번역 결과에서 RequestId 추출 - 이후 참조용
      const requestId = result.RequestId || result.requestId ||
                       (result.Data && result.Data.RequestId) || '';

      console.log(`[디버깅] 번역 완료, requestId: ${requestId}`);

      // 로깅용으로만 이미지 URL 추출 (실제로는 사용하지 않음)
      const translatedImageUrl = result.translatedImageUrl ||
                               (result.Data && result.Data.SslTranslateUrl) ||
                               (result.Data && result.Data.TranslateUrl);

      // 텍스트 제거 이미지 URL을 여러 가능한 위치에서 검색
      let textRemovedImageUrl = null;

      // 1. 기존 구조 확인 (translatedImagePixelData.repairedUrl)
      if (result.translatedImagePixelData && result.translatedImagePixelData.repairedUrl) {
        textRemovedImageUrl = result.translatedImagePixelData.repairedUrl;
      }
      // 2. Data 객체 내부 확인 (Data.RmUrl 또는 Data.SslRmUrl) - 새 API 응답 형식
      else if (result.Data) {
        textRemovedImageUrl = result.Data.SslRmUrl || result.Data.RmUrl;
      }
      // 3. 최상위 레벨 확인 (RmUrl 또는 SslRmUrl)
      else if (result.RmUrl || result.SslRmUrl) {
        textRemovedImageUrl = result.SslRmUrl || result.RmUrl;
      }

      console.log('[디버깅] API 응답에서 추출된 이미지 URL들 (로깅용):', {
        translatedImageUrl: translatedImageUrl || '없음',
        textRemovedImageUrl: textRemovedImageUrl || '없음',
        originalUrl: imageUrl
      });

      // 중요: 이미 URL이 있는 경우 다운로드 없이 직접 사용
      // 1. API가 유효한 URL을 반환한 경우 사용
      if (translatedImageUrl && translatedImageUrl.startsWith('http')) {
        console.log(`[디버깅] API가 반환한 URL을 직접 사용합니다: ${translatedImageUrl.substring(0, 50)}...`);

        // URL 직접 사용 (base64 변환 건너뛰기)
        return {
          originalUrl: imageUrl,
          url: translatedImageUrl, // API가 제공한 URL 사용
          name: `${store.state.user.suppliercode || store.state.user.companyid}_${productId}_detail.jpg`,
          processType: 'translated',
          translatedData: result, // 번역 데이터는 포함
          requestId: requestId, // RequestId 추가
          textAreas: result.translatedImagePixelData ? result.translatedImagePixelData.textAreas : [],
          isBase64: false // URL임을 표시 (base64 아님)
        };
      }

      // 2. 번역된 URL이 없으면 원본 이미지 사용
      const imageToUse = imageUrl; // 항상 원본 이미지만 사용
      console.log(`[디버깅] 이미지 다운로드에 사용할 URL: ${imageToUse.substring(0, 50)}...`);

      let base64Data;
      try {
        // 이미지 다운로드 및 base64 변환 시도
        const imageBlob = await imageService.downloadImage(imageToUse);
        base64Data = await imageService.blobToBase64(imageBlob);
        console.log('[디버깅] 이미지 다운로드 및 base64 변환 성공');
      } catch (error) {
        console.warn(`[디버깅] 이미지 다운로드 실패 (URL: ${imageToUse}), 원본 URL 그대로 사용: ${error.message}`);

        // 다운로드 실패 시 이미지 URL만 반환 (base64 데이터 없이)
        return {
          originalUrl: imageUrl,
          url: imageUrl, // 원본 URL 그대로 사용
          name: `${store.state.user.suppliercode || store.state.user.companyid}_${productId}_detail.jpg`,
          dimensions: '800x600', // 기본 크기
          originalWidth: 800,
          originalHeight: 600,
          processType: 'translated',
          translatedData: result, // 번역 데이터는 포함
          requestId: requestId, // RequestId 추가
          textAreas: result.translatedImagePixelData ? result.translatedImagePixelData.textAreas : [],
          isBase64: false // URL임을 표시 (base64 아님)
        };
      }

      // 이미지 크기 가져오기
      const imageDimensions = await this.getImageDimensions(imageToUse);
      console.log('[디버깅] 이미지 원본 크기:', imageDimensions);

      // 파일명 생성 (나중에 S3 업로드 시 사용)
      const supplierCode = store.state.user.suppliercode || store.state.user.companyid;
      const fileName = `${supplierCode}_${productId}_detail.jpg`;

      // 이미지 정보 반환 (base64 데이터 포함, S3URL은 없음)
      return {
        originalUrl: imageUrl,
        url: base64Data, // base64Data를 직접 URL로 사용
        name: fileName, // 나중에 업로드할 때 사용할 파일명
        dimensions: `${imageDimensions.width}x${imageDimensions.height}`,
        originalWidth: imageDimensions.width,
        originalHeight: imageDimensions.height,
        filesize: this.estimateFileSize(base64Data),
        processType: 'translated',
        translatedData: result, // 번역 데이터 전체 저장
        textAreas: result.translatedImagePixelData ? result.translatedImagePixelData.textAreas : [],
        isBase64: true // base64 데이터임을 표시하는 플래그 추가
      };
    } catch (error) {
      console.error('이미지 처리 중 오류:', error);
      return null;
    }
  },

  /**
   * base64 이미지를 S3에 업로드하여 URL로 변환
   * @param {Array} processedImages - base64 이미지 정보 배열
   * @returns {Promise<Array>} S3 URL로 변환된 이미지 정보 배열
   */
  async uploadProcessedImagesToS3(processedImages) {
    console.log(`[업로드 디버깅] uploadProcessedImagesToS3 시작 - ${processedImages.length}개 이미지`);
    console.trace(`[업로드 디버깅] uploadProcessedImagesToS3 호출 스택 추적`);

    const uploadedImages = [];
    let base64Count = 0;
    let urlCount = 0;

    // 이미지 유형 카운트
    processedImages.forEach(image => {
      if (image.isBase64 && image.url) {
        base64Count++;
      } else {
        urlCount++;
      }
    });

    console.log(`[업로드 디버깅] 이미지 유형 분석: base64=${base64Count}개, URL=${urlCount}개`);

    // 각 이미지 처리
    for (let i = 0; i < processedImages.length; i++) {
      const image = processedImages[i];

      try {
        // base64 이미지인 경우 S3에 업로드
        if (image.isBase64 && image.url) {
          console.log(`[업로드 디버깅] 이미지 ${i+1}/${processedImages.length} S3 업로드 시작 (base64)`);

          // 파일명에 확장자 확인
          let fileName = image.name;
          if (!fileName.match(/\.(jpg|jpeg|png|gif)$/i)) {
            fileName += '.jpg'; // 확장자가 없으면 .jpg 추가
          }

          // 중요: base64가 실제로 base64 데이터인지 확인
          if (!image.url.startsWith('data:')) {
            console.warn(`[업로드 디버깅] 이미지 ${i+1}는 isBase64=true로 표시되었지만 실제로는 base64 형식이 아닙니다. URL로 처리합니다.`);
            uploadedImages.push({
              ...image,
              isBase64: false  // 실제로는 base64가 아니므로 수정
            });
            continue;  // 다음 이미지로 넘어감
          }

          // S3에 업로드 - 타입과 파일명 로깅 강화
          console.log(`[업로드 디버깅] S3 업로드 호출 - 타입: detail, 파일명: ${fileName}`);
          const uploadResult = await imageService.uploadImageToS3(
            image.url, // base64 데이터
            fileName, // 파일명
            'detail' // 업로드 타입
          );

          // 업로드 결과 처리 (객체 또는 문자열)
          let s3ImageUrl;
          if (uploadResult && typeof uploadResult === 'object') {
            // 객체 형태로 반환된 경우 (신규 형식)
            if (uploadResult.imageUrl) {
              s3ImageUrl = uploadResult.imageUrl;
            } else {
              console.error(`[업로드 디버깅] 이미지 ${i+1} 업로드 결과에 imageUrl이 없습니다:`, uploadResult);
              continue; // 다음 이미지로 넘어감
            }
          } else if (typeof uploadResult === 'string') {
            // 문자열로 반환된 경우 (이전 형식)
            s3ImageUrl = uploadResult;
          } else {
            console.error(`[업로드 디버깅] 이미지 ${i+1} 알 수 없는 업로드 결과 형식:`, uploadResult);
            continue; // 다음 이미지로 넘어감
          }

          console.log(`[업로드 디버깅] 이미지 ${i+1} S3 업로드 완료: ${s3ImageUrl.substring(0, 50)}...`);

          // 업로드된 이미지 정보 저장 - URL로 변환
          uploadedImages.push({
            ...image,
            url: s3ImageUrl, // base64를 S3 URL로 교체
            isBase64: false, // 더 이상 base64가 아님
            originalUrl: image.originalUrl || image.url.substring(0, 100) // 원본 URL 유지
          });
        } else {
          // 이미 URL인 경우 그대로 유지
          console.log(`[업로드 디버깅] 이미지 ${i+1}/${processedImages.length}는 이미 URL 형식 - 그대로 유지`);

          // 이미 URL 형식인 경우 originalUrl 필드 확인
          if (!image.originalUrl && image.url) {
            // originalUrl이 없으면 현재 URL을 originalUrl로 설정
            image.originalUrl = image.url;
          }

          uploadedImages.push(image);
        }
      } catch (error) {
        console.error(`[업로드 디버깅] 이미지 ${i+1}/${processedImages.length} 업로드 중 오류:`, error);
        logS3UploadError('detailImageService', 'uploadProcessedImagesToS3', error, {
          fallbackAction: '원본 URL 사용',
          additional: { imageIndex: i, totalImages: processedImages.length }
        });

        // 오류 세부 정보 로깅
        if (error.response) {
          console.error('[업로드 디버깅] S3 업로드 API 응답 오류:', {
            status: error.response.status,
            data: error.response.data
          });
        }

        // 오류 발생 시 원본 URL 사용
        // URL 형식이 아니면 originalUrl 사용 (있을 경우)
        const fallbackUrl = image.originalUrl || image.url;
        console.log(`[업로드 디버깅] 이미지 ${i+1} 오류로 인해 URL 대체: ${fallbackUrl.substring(0, 30)}...`);

        // 기존 이미지 객체는 유지하되 URL 필드만 변경
        uploadedImages.push({
          ...image,
          url: fallbackUrl,
          uploadError: error.message // 오류 정보 추가
        });
      }
    }

    console.log(`[업로드 디버깅] uploadProcessedImagesToS3 완료 - ${uploadedImages.length}개 이미지 처리됨`);
    return uploadedImages;
  },

  /**
   * HTML 내의 base64 이미지를 S3 URL로 교체
   * @param {string} html - 교체할 HTML 문자열
   * @param {Object} imageMap - base64 이미지와 S3 URL 매핑 객체
   * @returns {string} 교체된 HTML 문자열
   */
  replaceBase64WithS3Urls(html, imageMap) {
    let updatedHtml = html;

    // 각 base64 이미지를 S3 URL로 교체
    Object.keys(imageMap).forEach(base64Prefix => {
      // base64 문자열은 길기 때문에 시작 부분만 매칭
      const regex = new RegExp(base64Prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      updatedHtml = updatedHtml.replace(regex, imageMap[base64Prefix]);
    });

    return updatedHtml;
  },

  /**
   * 이미지 크기 가져오기
   * @param {string} imageUrl - 이미지 URL
   * @returns {Promise<{width: number, height: number}>} - 이미지 크기
   */
  getImageDimensions(imageUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = (err) => {
        console.error('이미지 크기 로드 오류:', err);
        // 오류 시 기본 크기 반환
        resolve({
          width: 800,
          height: 600
        });
      };
      img.src = imageUrl;
    });
  },

  /**
   * 파일 크기 추정
   * @param {string} base64Data - Base64 인코딩된 데이터
   * @returns {string} - 포맷팅된 파일 크기
   */
  estimateFileSize(base64Data) {
    if (!base64Data) return '0 KB';

    // Base64 문자열에서 데이터 부분만 추출
    const base64 = base64Data.split(',')[1];
    if (!base64) return '0 KB';

    // Base64 길이를 바이트 크기로 변환 (대략 3/4)
    const sizeInBytes = Math.ceil((base64.length * 3) / 4);

    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${Math.round(sizeInBytes / 1024)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  },

  /**
   * 이미지 텍스트 번역 API 호출
   * @param {string} imageUrl - 이미지 URL
   * @returns {Promise<Object>} API 응답 결과
   */
  async translateImage(imageUrl) {
    try {
      console.log(`translateImage 시작 - 이미지 URL: ${imageUrl.substring(0, 50)}...`);

      // API 호출 시간 기록
      const startTime = Date.now();

      const requestData = [{ imgUrl: imageUrl }];
      const response = await apiService.post('/api/aiPage/getImageTranslate', requestData);

      // API 응답 시간 측정
      const endTime = Date.now();
      console.log(`translateImage API 응답 시간: ${endTime - startTime}ms`);

      if (response.요청결과 === "성공" && response.result && response.result.length > 0) {
        const result = response.result[0];

        // 원본 이미지 URL을 결과에 추가 (나중에 참조용)
        result.originalImageUrl = imageUrl;

        // 응답 형식 변환 및 일관된 필드 구조 생성
        // 서로 다른 API 응답 형식을 통합
        if (result.Data) {
          // Data 객체가 있는 경우 (새 API 형식)
          if (!result.translatedImagePixelData) {
            result.translatedImagePixelData = {};
          }

          // 모든 이미지 URL 정보 로깅
          console.log('API 응답의 이미지 URL 구조:', {
            'Data.Url': result.Data.Url || '없음',
            'Data.SslUrl': result.Data.SslUrl || '없음',
            'Data.RmUrl': result.Data.RmUrl || '없음',
            'Data.SslRmUrl': result.Data.SslRmUrl || '없음',
            'Data.TranslateUrl': result.Data.TranslateUrl || '없음',
            'Data.SslTranslateUrl': result.Data.SslTranslateUrl || '없음'
          });

          // 텍스트 제거 이미지 URL 설정
          if (result.Data.RmUrl || result.Data.SslRmUrl) {
            result.translatedImagePixelData.repairedUrl = result.Data.SslRmUrl || result.Data.RmUrl;
          }

          // 번역된 이미지 URL 설정
          if (result.Data.SslUrl || result.Data.Url) {
            result.translatedImageUrl = result.Data.SslUrl || result.Data.Url;
          } else if (result.Data.SslTranslateUrl || result.Data.TranslateUrl) {
            result.translatedImageUrl = result.Data.SslTranslateUrl || result.Data.TranslateUrl;
          }

          // RequestId (대문자) 처리 - 일관된 필드명으로 변환
          if (result.RequestId && !result.requestId) {
            result.requestId = result.RequestId;
          } else if (result.Data.RequestId && !result.requestId) {
            result.requestId = result.Data.RequestId;
          }
        }

        // 주요 URL 정보 로깅
        console.log('변환된 translateImage 결과 요약:', {
          requestId: result.requestId || result.RequestId || '없음',
          originalImageUrl: imageUrl,
          translatedImageUrl: result.translatedImageUrl || '없음',
          repairedUrl: result.translatedImagePixelData?.repairedUrl || '없음'
        });

        return result;
      }

      console.warn('translateImage API 응답 실패:', response);
      return null;
    } catch (error) {
      console.error('이미지 번역 API 오류:', error);

      // 오류 세부 정보 로깅
      if (error.response) {
        console.error('API 응답 오류 상세:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }

      throw error;
    }
  },

  // HTML 컨텐츠 생성 함수 제거 (더 이상 사용되지 않음)
  // // detailImageService.js의 generateHtmlContent 메서드 수정
  // generateHtmlContent(image, productName, imageMargin = 20) {
  //   // 텍스트 영역이 없으면 기본 이미지만 반환
  //   if (!image.textAreas || image.textAreas.length === 0) {
  //     return `<div style="margin-bottom: ${imageMargin}px; text-align: center;">
  //         <img src="${image.url}" alt="${productName}" style="max-width: 100%; height: auto; display: block; margin: 0 auto;">
  //     </div>`;
  //   }

  //   // 이미 저장된 HTML 결과가 있는지 확인
  //   const existingResult = store.state.detailHtmlResults &&
  //     store.state.detailHtmlResults[image.url];

  //   if (existingResult) {
  //     console.log('이미 생성된 HTML 결과 사용:', image.url);
  //     return existingResult;
  //   }



  //   // 기본 HTML 생성하여 즉시 반환 (나중에 응답이 오면 DOM 업데이트)
  //   return `<div style="margin-bottom: ${imageMargin}px; text-align: center;" class="detail-image-container" id="${uniqueId}">
  //     <img src="${image.url}" alt="${productName}" style="max-width: 100%; height: auto; display: block; margin: 0 auto;">
  //     <div class="detail-html-loading" style="padding: 8px; margin-top: 10px; background: #f8f9fa; border-radius: 4px;
  //         font-size: 12px; color: #6c757d; text-align: center;">
  //         AI가 상세 내용을 생성 중입니다...
  //     </div>
  // </div>`;
  // },


  // 전체 상세 페이지 HTML 생성 함수 제거 (더 이상 사용되지 않음)

  /**
   * HTML에서 이미지 URL 추출
   * @param {string} html - HTML 문자열
   * @returns {Array} 추출된 이미지 URL 배열
   */
  extractImagesFromHtml(html) {
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    const urls = [];
    let match;

    while ((match = imgRegex.exec(html)) !== null) {
      if (match[1] && match[1].length > 10) {
        urls.push(match[1]);
      }
    }

    return urls;
  },
  /**
   * HTML을 이미지로 변환하여 S3에 업로드
   * @param {string} html - 변환할 HTML 문자열
   * @param {string} productId - 상품 ID
   * @param {{throwOnError?: boolean}} [opts] - throwOnError 면 실패 때 null 대신 까닭(서버 응답 포함)을 던진다
   * @returns {Promise<string>} 업로드된 이미지 URL
   */
  async convertHtmlToImage(html, productId, { throwOnError = false } = {}) {
    try {
      console.log('[디버깅] convertHtmlToImage >>> 변환 시작')
      console.trace('[디버깅] convertHtmlToImage 호출 스택 추적')

      // 1) 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '상세 HTML을 이미지로 변환 중...'
      })

      // 2) 임시 컨테이너 생성
      const container = document.createElement('div')
      container.innerHTML = html
      container.style.position = 'absolute'
      container.style.top = '-9999px'
      container.style.left = '0'
      container.style.width = '800px' // 캡처 용도라 800px로 고정
      container.style.backgroundColor = 'white' // 배경색 흰색으로 변경
      container.style.padding = '0' // 패딩 제거
      container.style.margin = '0' // 마진 제거
      container.style.boxSizing = 'border-box' // 박스 모델 설정
      document.body.appendChild(container)

      // 컨테이너 내 모든 이미지에 고정 너비 800px 적용
      const allImages = container.querySelectorAll('img')
      console.log(`컨테이너 내 이미지 ${allImages.length}개 사이즈 조정 중...`)

      allImages.forEach((img, idx) => {
        img.style.maxWidth = '100%'
        img.style.width = '800px'
        img.style.height = 'auto'
        img.style.display = 'block'
        img.style.margin = '0 auto'
        img.setAttribute('width', '800')

        console.log(`이미지 ${idx+1}/${allImages.length} 너비 800px로 설정`)
      })

      // 3) 모든 이미지가 로드될 때까지 대기
      console.log('모든 이미지 로드 대기 중...')
      await this.waitForAllImagesToLoad(container)
      console.log('모든 이미지 로드 완료, 캡처 시작')

      // 4) html2canvas로 캡처
      console.log('html2canvas 캡처 시작')
      const canvas = await withTimeout(html2canvas(container, {
        // 복제는 이 div·조상·같은 출처 css 만 — 화면 전체 복제는 안 끝나는 것 하나에 3분 한도까지 탔다(윈 3517 PR261021-17E230)
        ignoreElements: cloneOnly(container),
        useCORS: true,
        logging: true,
        scale: 1.0, // 스케일 1.0으로 설정
        backgroundColor: '#FFFFFF', // 흰색 배경
        width: 800, // 너비 800px로 고정
        allowTaint: true, // 크로스 도메인 이미지 허용
        imageTimeout: 10000, // 이미지 로딩 타임아웃 10초로 증가
        onclone: (clonedDoc) => {
          // 복제된 문서에서 이미지 크기 한번 더 강제 조정
          const images = clonedDoc.querySelectorAll('img')
          console.log(`클론 문서 내 이미지 ${images.length}개 사이즈 재조정`)

          images.forEach(img => {
            img.style.maxWidth = '100%'
            img.style.width = '800px'
            img.style.height = 'auto'
            img.style.display = 'block'
            img.style.margin = '0 auto'
            img.setAttribute('width', '800')
          })
        }
      }), H2C_TIMEOUT_MS, '상세 이미지 그리기')

      // 5) base64 데이터 얻기
      console.log('캔버스를 이미지로 변환 중...')
      const imageData = canvas.toDataURL('image/jpeg', 0.95) // 품질 향상 (0.95)

      if (!imageData || imageData.length < 2000) {
        console.error('[convertHtmlToImage] ***에러*** 이미지 데이터가 너무 작거나 없음 (빈 이미지?)')
        if (throwOnError) throw new Error('통합 이미지가 비었어요(그림 데이터 없음)')
        return null
      }

      // 6) S3 업로드
      console.log('[디버깅] convertHtmlToImage - S3 업로드 시작...')
      const supplierCode = store.state.user.suppliercode || store.state.user.companyid
      const fileName = `${supplierCode}_${productId}_detail_full_${Date.now()}.jpg`

      console.log(`[디버깅] convertHtmlToImage - 이미지 S3 업로드 호출 - 파일명: ${fileName}`)
      try {
        const uploadResult = await imageService.uploadImageToS3(
          imageData,
          fileName,
          'detail'
        )

        // 7) 임시 컨테이너 제거
        document.body.removeChild(container)
        console.log('임시 컨테이너 제거 완료')

        // 반환 형식 확인 및 처리
        if (uploadResult && typeof uploadResult === 'object') {
          // 객체 형태로 반환된 경우 (신규 형식)
          if (uploadResult.imageUrl) {
            console.log(`[디버깅] convertHtmlToImage - S3 업로드 완료. URL: ${uploadResult.imageUrl}`)
            return uploadResult.imageUrl
          } else {
            console.error('[디버깅] convertHtmlToImage - 업로드 결과에 imageUrl이 없습니다:', uploadResult)
            if (throwOnError) throw new Error('S3 업로드 결과에 이미지 주소가 없어요')
            return null
          }
        } else if (typeof uploadResult === 'string') {
          // 문자열로 반환된 경우 (이전 형식)
          console.log(`[디버깅] convertHtmlToImage - S3 업로드 완료. URL: ${uploadResult}`)
          return uploadResult
        } else {
          console.error('[디버깅] convertHtmlToImage - 알 수 없는 업로드 결과 형식:', uploadResult)
          return null
        }
      } catch (uploadError) {
        console.error('[디버깅] convertHtmlToImage - S3 업로드 중 오류:', uploadError)

        // 임시 컨테이너 제거 (오류 발생해도)
        if (document.body.contains(container)) {
          document.body.removeChild(container)
          console.log('오류 발생 후 임시 컨테이너 제거 완료')
        }

        if (throwOnError) throw uploadError
        return null
      }
    } catch (error) {
      console.error('[convertHtmlToImage] 변환 오류:', error)
      if (throwOnError) throw error
      return null
    } finally {
      // 로딩 상태 해제
      store.dispatch('setProcessing', {
        isProcessing: false,
        message: ''
      })
    }
  },

  /**
   * 컨테이너 내의 모든 이미지가 로드될 때까지 기다림
   * @param {HTMLElement} container - 이미지를 포함한 컨테이너
   * @returns {Promise<void>}
   */
  /**
   * 컨테이너 내의 모든 이미지가 로드될 때까지 기다림
   * @param {HTMLElement} container - 이미지를 포함한 컨테이너
   * @returns {Promise<void>}
   */
  waitForAllImagesToLoad(container) {
    return new Promise((resolve) => {
      const images = container.querySelectorAll('img')

      // 이미지가 없으면 바로 완료
      if (images.length === 0) {
        console.log('대기할 이미지가 없습니다.')
        resolve()
        return
      }

      console.log(`총 ${images.length}개 이미지 로드 대기 중...`)

      let loadedCount = 0
      const totalCount = images.length

      // 각 이미지에 로드 이벤트 추가
      images.forEach((img, idx) => {
        // img 스타일 강제 적용 (800px 고정 너비)
        img.style.maxWidth = '100%'
        img.style.width = '800px'
        img.style.height = 'auto'

        if (img.complete) {
          loadedCount++
          console.log(`이미지 ${idx+1}/${totalCount} 이미 로드됨, 현재 ${loadedCount}/${totalCount} 완료`)

          if (loadedCount === totalCount) {
            console.log('모든 이미지 로드 완료')
            resolve()
          }
        } else {
          img.onload = () => {
            loadedCount++
            console.log(`이미지 ${idx+1}/${totalCount} 로드 완료, 현재 ${loadedCount}/${totalCount} 완료`)

            if (loadedCount === totalCount) {
              console.log('모든 이미지 로드 완료')
              resolve()
            }
          }

          img.onerror = () => {
            loadedCount++
            console.warn(`이미지 ${idx+1}/${totalCount} 로드 실패: ${img.src.substring(0, 50)}...`)

            if (loadedCount === totalCount) {
              console.log('모든 이미지 로드 완료 (일부 실패)')
              resolve()
            }
          }
        }
      })

      // 안전장치: 10초 후에 무조건 진행 (시간 증가)
      setTimeout(() => {
        if (loadedCount < totalCount) {
          console.warn(`일부 이미지(${totalCount - loadedCount}/${totalCount})가 로드되지 않았지만 타임아웃으로 계속 진행합니다.`)
          resolve()
        }
      }, 10000) // 10초로 증가 (복잡한 이미지 로드 시간 충분히 확보)
    })
  },

  /**
   * 디버깅/미리보기를 위해, 캡처 결과 이미지를 화면에 표시하기
   * @param {string} base64Image
   */
  // showPreviewInPanel(base64Image) {
  //   try {
  //     // 이미 미리보기 영역이 있는지 찾기
  //     let previewPanel = document.getElementById('capture-preview-panel')
  //     if (!previewPanel) {
  //       // 없으면 새로 생성
  //       previewPanel = document.createElement('div')
  //       previewPanel.id = 'capture-preview-panel'

  //       // 간단한 스타일
  //       previewPanel.style.position = 'fixed'
  //       previewPanel.style.bottom = '20px'
  //       previewPanel.style.right = '20px'
  //       previewPanel.style.width = '200px'
  //       previewPanel.style.height = '200px'
  //       previewPanel.style.overflow = 'auto'
  //       previewPanel.style.border = '2px solid #aaa'
  //       previewPanel.style.background = '#fff'
  //       previewPanel.style.zIndex = '9999'
  //       previewPanel.style.padding = '5px'
  //       previewPanel.innerHTML = `<p style="font-size:12px;">캡처 미리보기 영역</p>`

  //       document.body.appendChild(previewPanel)
  //     }

  //     // 미리보기 이미지 태그 추가
  //     const previewImg = document.createElement('img')
  //     previewImg.src = base64Image
  //     previewImg.style.width = '100%'
  //     previewImg.style.height = 'auto'
  //     previewImg.style.display = 'block'
  //     previewImg.style.marginTop = '8px'

  //     // 현재 시각 표기
  //     const time = new Date().toLocaleTimeString()
  //     const label = document.createElement('div')
  //     label.textContent = `미리보기 (${time})`
  //     label.style.fontSize = '12px'
  //     label.style.color = '#333'
  //     label.style.marginTop = '4px'

  //     previewPanel.appendChild(label)
  //     previewPanel.appendChild(previewImg)
  //     console.log('[showPreviewInPanel] Appended preview image')
  //   } catch (err) {
  //     console.error('[showPreviewInPanel] Error:', err)
  //   }
  // },

  /**
   * 상세 이미지 처리 및 저장 메서드
   * 작업 개시 버튼으로 호출될 때 처리부터 저장까지 자동화
   * @param {Object} product - 상품 객체
   * @returns {Promise<Object>} 처리 결과
   */
  /**
   * 상세 이미지 처리 및 저장 메서드 - 메인 엔트리 포인트
   * (DetailImageEdit.vue의 "모든 이미지 처리" + "저장" 흐름과 동일하게 구현)
   * @param {Object} product - 상품 객체
   * @returns {Promise<Object>} 처리 결과
   */
  async process(product) {
    console.log(`[중요 디버깅] process 메서드 시작 - 상품 ID: ${product.registrationId}`);
    console.trace('[중요 디버깅] process 메서드 호출 스택 추적');

    try {
      // 1. 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '상세 이미지 처리 중...'
      });

      // 1-1. altText 초기 설정 (brand + productFeature + targetKeyword)
      const altTextParts = [];
      if (product.brand) altTextParts.push(product.brand);
      if (product.productFeature) altTextParts.push(product.productFeature);
      if (product.targetKeyword) altTextParts.push(product.targetKeyword);
      // 한자·전각 괄호·«1688» 은 걸러 낸다 — 견적서 최종 검증(schemaValidator.containsChinese)이 대체 텍스트의 한자를 막는다 · 거름은 detailAltText.js 한 곳
      let initialAltText = stripAltHan(altTextParts.join(' '));
      console.log(`altText 초기 설정: "${initialAltText}" (${initialAltText.length}자)`);

      // 상세 이미지 URL 확인
      const detailImages = product.detailImages || [];
      console.log(`상세 이미지 개수: ${detailImages.length}`);
      if (detailImages.length > 0) {
        console.log(`첫 번째 이미지 URL: ${detailImages[0].substring(0, 50)}...`);
      }

      // 예외 처리: 이미지가 없는 경우
      // 상세 사진이 0장이면 만들 것이 없다 — 'completed' 로 돌려주면 상태를 아무도 안 써서 'processing' 에 영구히 머물고,
      // 견적서는 상세이미지·대체텍스트 칸이 비어 멈춘다(PR261017-305255). 이유를 남기고 'error' 로 돌려준다
      // (상태 'error' 는 workflowService.executeSingleTask 가 쓴다).
      const zeroResult = zeroDetailImageResult(product);
      if (zeroResult) {
        console.error(`[상세이미지] ${zeroResult.error} (상품: ${product.registrationId})`);
        try {
          await store.dispatch('updateProductPartial', {
            productId: product._id,
            updateFields: { detailImageWarning: { message: zeroResult.error, failedCount: 0, total: 0, at: Date.now() } }
          });
        } catch (err) {
          console.error('[상세이미지] 0장 사유 저장 실패:', err);
        }
        return zeroResult;
      }

      // ======== DetailImageEdit의 processAllImages 흐름 구현 시작 ========

      // 2. 이미 처리된 이미지 확인 (DetailImageEdit과 동일한 로직)
      const imagesToProcess = [];
      const processedGroupsMap = {}; // 처리된 이미지 저장용 맵

      // 이미 처리된 이미지 확인 - product.processedDetailImages에서 데이터 추출
      if (product.processedDetailImages && product.processedDetailImages.length > 0) {
        console.log(`기존 처리된 이미지 확인: ${product.processedDetailImages.length}개`);

        // processedGroupsMap 초기화
        product.processedDetailImages.forEach(img => {
          if (img && img.index !== undefined) {
            processedGroupsMap[img.index] = img;
          }
        });

        console.log(`기존 처리된 이미지 맵 생성 완료: ${Object.keys(processedGroupsMap).length}개`);
      }

      // 이미 처리된 이미지인지 확인 - DetailImageEdit.vue processAllImages 로직과 동일
      for (let i = 0; i < detailImages.length; i++) {
        const imageUrl = detailImages[i];

        // 이미 처리된 이미지인지 확인 (requestId 있는지)
        const existingProcessed = processedGroupsMap[i];

        // 번역 실패로 원본을 쓴 이미지(translationFailed)는 다시 돌린다 — requestId 가 있어도 건너뛰지 않는다
        if (existingProcessed && existingProcessed.requestId && !existingProcessed.translationFailed) {
          console.log(`이미지 인덱스 ${i}는 이미 처리됨 (requestId: ${existingProcessed.requestId}). 건너뜀`);
        } else {
          // 처리할 이미지 목록에 추가
          imagesToProcess.push({
            url: imageUrl,
            index: i
          });
        }
      }

      // 처리할 이미지가 없는 경우
      if (imagesToProcess.length === 0) {
        console.log('모든 이미지가 이미 처리되었습니다. 저장 작업만 진행합니다.');
      } else {
        console.log(`처리할 이미지 ${imagesToProcess.length}개:`,
          imagesToProcess.map(img => img.index).join(', '));
      }

      // 3. 이미지 처리 - DetailImageEdit의 translateSingleImage 사용
      // (processImage가 아닌 translateSingleImage를 사용) - 병렬 처리
      const newlyProcessedImages = [];

      // 병렬 처리를 위한 동시 실행 개수 제한
      const CONCURRENT_LIMIT = 3;

      // 이미지별 실패 사유 — 번역 API 자체가 실패해 결과가 없는 경우(null 반환) 여기에 남긴다
      const failReasons = {};

      // 단일 이미지 처리 함수 (병렬 실행용)
      const processSingleDetailImage = async (imageInfo) => {
        try {
          console.log(`[중요 디버깅] translateSingleImage 호출 - 인덱스: ${imageInfo.index}, URL: ${imageInfo.url.substring(0, 50)}...`);
          const processedImage = await this.translateSingleImage(
            imageInfo.url,
            imageInfo.index,
            claimBasisOf(product)
          );

          if (processedImage) {
            console.log(`[중요 디버깅] 이미지 처리 완료 - 인덱스: ${imageInfo.index}, isBase64: ${!!processedImage.isBase64}, URL 타입: ${typeof processedImage.url}`);

            if (processedImage.isBase64 && processedImage.url) {
              console.log(`[중요 디버깅] base64 데이터 확인 - 시작 부분: ${typeof processedImage.url === 'string' ? processedImage.url.substring(0, 30) : '형식 오류'}`);
            }
            return processedImage;
          }
          return null;
        } catch (error) {
          console.error(`[중요 디버깅] 이미지 인덱스 ${imageInfo.index} 처리 중 오류:`, error);
          failReasons[imageInfo.index] = `번역 요청 실패: ${error && error.message ? error.message : '알 수 없는 오류'}`;
          return null;
        }
      };

      // 병렬 처리 (동시 CONCURRENT_LIMIT개씩). 결과가 없거나 원본으로 대체된(translationFailed) 이미지 목록을 돌려준다
      const runBatches = async (list, label) => {
        const failedList = [];
        for (let i = 0; i < list.length; i += CONCURRENT_LIMIT) {
          const batch = list.slice(i, i + CONCURRENT_LIMIT);

          // 진행 상태 업데이트
          store.dispatch('setProcessing', {
            isProcessing: true,
            message: `${label}... (${Math.min(i + CONCURRENT_LIMIT, list.length)}/${list.length})`
          });

          setStepCount(product.registrationId, 'detailImage', i, list.length);
          // 배치 병렬 처리
          const batchResults = await Promise.allSettled(
            batch.map(imageInfo => processSingleDetailImage(imageInfo))
          );

          batchResults.forEach((result, k) => {
            const value = result.status === 'fulfilled' ? result.value : null;
            if (value) {
              // 원본으로 대체된 것도 일단 맵에 둔다 — 재시도까지 실패하면 이것이 남는다
              processedGroupsMap[value.index] = value;
              if (value.translationFailed) {
                failedList.push(batch[k]);
              } else {
                newlyProcessedImages.push(value);
              }
            } else {
              if (result.status === 'rejected') {
                failReasons[batch[k].index] = `번역 요청 실패: ${result.reason && result.reason.message ? result.reason.message : '알 수 없는 오류'}`;
              }
              failedList.push(batch[k]);
            }
          });
          setStepCount(product.registrationId, 'detailImage', Math.min(i + CONCURRENT_LIMIT, list.length), list.length);
        }
        return failedList;
      };

      console.log(`상세 이미지 ${imagesToProcess.length}개 병렬 처리 시작 (동시 ${CONCURRENT_LIMIT}개)`);
      let failedImages = await runBatches(imagesToProcess, '상세 이미지 처리 중');

      // 실패한 이미지만 잠시 뒤 1회 재시도 — 연결이 순간 끊긴 경우(502 등) 대부분 여기서 살아난다
      if (failedImages.length > 0) {
        const RETRY_DELAY_MS = 3000;
        console.warn(`[상세이미지] 번역 실패 ${failedImages.length}장 (인덱스: ${failedImages.map(f => f.index).join(', ')}) — ${RETRY_DELAY_MS}ms 뒤 1회 재시도`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        failedImages.forEach(f => { delete failReasons[f.index]; });
        failedImages = await runBatches(failedImages, '상세 이미지 번역 재시도 중');
      }

      // 재시도 후에도 남은 실패 — 완료로 찍지 않고 장수·사유를 남긴다
      const translationFailures = failedImages.map(f => {
        const kept = processedGroupsMap[f.index];
        return {
          index: f.index,
          reason: (kept && kept.failReason) || failReasons[f.index] || '알 수 없는 오류',
          // true = 원본(미번역) 이미지가 상세페이지에 들어갔다 / false = 이미지가 빠졌다
          usedOriginal: !!(kept && kept.translationFailed)
        };
      });
      if (translationFailures.length > 0) {
        console.error(`[상세이미지] 재시도 후에도 번역 실패 ${translationFailures.length}/${detailImages.length}장:`, translationFailures);
      }

      console.log(`[중요 디버깅] 이미지 병렬 처리 완료: ${newlyProcessedImages.length}개 새로 처리됨`);

      // ======== DetailImageEdit의 저장 흐름 구현 시작 ========

      // 4. 저장 시작
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '상세 이미지 저장 중...'
      });

      // processedGroupsMap에서 모든 처리된 이미지 추출 (객체를 배열로 변환)
      const allProcessedImages = Object.values(processedGroupsMap);
      console.log(`총 처리된 이미지 ${allProcessedImages.length}개 저장 시작`);

      // 5. HTML 생성 및 이미지 병합
      console.log('상세 이미지 병합 HTML 생성 시작...');

      // 설정에서 상단/하단 이미지 정보 가져오기 (있으면)
      const settings = store.state.settings && store.state.settings[0] &&
        store.state.settings[0].defaultSettings &&
        store.state.settings[0].defaultSettings.imageSettings || {};

      // 병합용 HTML 생성 - 정확히 800px 너비로 설정
      let combinedHtml = `
        <div style="width:800px; margin:0; padding:0; background-color:#ffffff; box-sizing:border-box;">
      `;

      // 상단 이미지 추가 (있으면) - 다운로드용 간격 0px로 설정
      if (settings.useTopImage && settings.topImageUrl) {
        console.log('상단 이미지 추가:', settings.topImageUrl.substring(0, 50) + '...');
        combinedHtml += `
        <div style="margin:0; padding:0; text-align:center; width:800px;">
          <img src="${settings.topImageUrl}" alt="상단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
        </div>
        `;
      }

      // 상단/하단 이미지 URL 가져오기
      const topImageUrl = settings.useTopImage && settings.topImageUrl ? settings.topImageUrl : null;
      const bottomImageUrl = settings.useBottomImage && settings.bottomImageUrl ? settings.bottomImageUrl : null;

      // 처리된 이미지만 추가 (상단/하단 이미지 제외)
      const contentImages = allProcessedImages.filter(image => {
        // 이미지 URL이 없는 경우 제외
        if (!image || !image.url) return false;

        // 상단/하단 이미지인지 확인
        const isTopOrBottomImage = (() => {
          const imageUrl = image.url;

          // 1. URL 직접 비교
          if (imageUrl === topImageUrl || imageUrl === bottomImageUrl) {
            return true;
          }

          // 2. 파일명에 topimage 또는 bottomimage 포함 여부 확인
          try {
            // URL에서 파일명 추출
            const urlObj = new URL(imageUrl);
            const pathname = urlObj.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

            // 파일명에 topimage 또는 bottomimage 포함 여부 확인
            return filename.includes('topimage') || filename.includes('bottomimage');
          } catch (error) {
            // URL 파싱 오류 시 기존 방식 결과 반환
            return false;
          }
        })();

        // 상단/하단 이미지가 아닌 경우만 포함
        return !isTopOrBottomImage;
      });

      console.log(`통합 이미지에 추가할 컨텐츠 이미지: ${contentImages.length}개`);

      // 모든 이미지를 가로 800px로 표시 (정확히 800px) - 다운로드용 간격 0px로 설정
      contentImages.forEach((image, index) => {
        const imageUrl = image.url || (image.originalUrl || '');
        if (imageUrl) {
          console.log(`이미지 ${index+1} 추가:`, imageUrl.substring(0, 50) + '...');
          combinedHtml += `
          <div style="margin:0; padding:0; text-align:center; width:800px;">
            <img src="${imageUrl}" alt="상세 이미지 ${index+1}"
                 style="width:800px; display:block; margin:0 auto; padding:0;"
                 width="800">
          </div>
          `;
        }
      });

      // 하단 이미지 추가 (있으면) - 다운로드용 간격 0px로 설정
      if (settings.useBottomImage && settings.bottomImageUrl) {
        console.log('하단 이미지 추가:', settings.bottomImageUrl.substring(0, 50) + '...');
        combinedHtml += `
        <div style="margin:0; padding:0; text-align:center; width:800px;">
          <img src="${settings.bottomImageUrl}" alt="하단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
        </div>
        `;
      }

      combinedHtml += '</div>';

      // 6. HTML을 단일 이미지로 변환
      console.log('HTML을 단일 이미지로 변환 시작 (800px 너비)');
      const combinedImageUrl = await this.convertHtmlToImage(combinedHtml, product.registrationId);

      let detailImageUrls = [];
      let detailHtml = '';

      if (!combinedImageUrl) {
        console.error('HTML을 이미지로 변환하는데 실패했습니다. 개별 이미지를 사용합니다. (폴백 방식)');

        // 실패 시 기존 방식으로 진행 (개별 이미지 업로드) - 폴백 메커니즘
        console.log('개별 이미지 업로드 방식으로 폴백...');

        // S3 업로드 준비
        const supplierCode = store.state.user.suppliercode || 'default';

        // 중요 로깅: 업로드 전 이미지 형식 확인
        console.log(`[중요 디버깅] S3 업로드 준비 전 이미지 형식 확인 (${allProcessedImages.length}개):`);
        allProcessedImages.forEach((img, idx) => {
          console.log(`[중요 디버깅] 이미지 ${idx}: isBase64=${!!img.isBase64}, URL 형식=${img.url?.substring(0, 20) || 'undefined'}`);
        });

        const uploadReady = allProcessedImages.map((image) => {
          // 파일명 형식 통일
          let fileName = `${supplierCode}_${product.registrationId}_detail_${image.index}.jpg`;

          // isBase64 플래그 확인 - 데이터 형식과 일치하는지 확인
          if (image.isBase64 && image.url && typeof image.url === 'string') {
            // 실제로 base64 데이터인지 검사
            const isReallyBase64 = image.url.startsWith('data:');
            if (!isReallyBase64) {
              console.warn(`[중요 디버깅] 경고: 이미지 ${image.index}는 isBase64=true로 표시되었지만 실제로는 base64 데이터가 아닙니다. 수정합니다.`);
              image.isBase64 = false;  // 플래그 수정
            }
          }

          return {
            ...image,
            name: fileName
          };
        });

        // S3 업로드 수행
        console.log(`[중요 디버깅] S3 개별 이미지 업로드 시작 - ${uploadReady.length}개 이미지`);
        console.log(`[중요 디버깅] uploadProcessedImagesToS3 함수 호출 시작`);
        const uploadedImages = await this.uploadProcessedImagesToS3(uploadReady);
        console.log(`[중요 디버깅] S3 개별 이미지 업로드 완료 - ${uploadedImages.length}개 이미지`);

        // 이미지 URL 목록 추출
        detailImageUrls = uploadedImages
          .filter(img => img.url && typeof img.url === 'string' && img.url.includes('://'))
          .map(img => img.url);

        if (detailImageUrls.length > 0) {
          console.log(`상세 이미지 URL 최종 개수: ${detailImageUrls.length}`);
          console.log(`첫 번째 최종 URL: ${detailImageUrls[0].substring(0, 50)}...`);
        }

        // HTML 생성 (기존 방식)
        if (detailImageUrls.length > 0) {
          detailHtml = `
          <div style="width:100%; max-width:800px; margin:0 auto; background-color:#ffffff; padding:0;">
          `;

          // 상단 이미지 추가 (있으면)
          if (settings.useTopImage && settings.topImageUrl) {
            detailHtml += `
            <div style="margin:0; padding:0; text-align:center;">
              <img src="${settings.topImageUrl}" alt="상단 이미지" style="max-width:100%; width:800px; display:block; margin:0 auto;">
            </div>
            `;
          }

          // 상세 이미지 추가 - 모두 800px 너비로 표시 (상단/하단 이미지 제외)
          // 상단/하단 이미지 URL 가져오기
          const fallbackTopImageUrl = settings.useTopImage && settings.topImageUrl ? settings.topImageUrl : null;
          const fallbackBottomImageUrl = settings.useBottomImage && settings.bottomImageUrl ? settings.bottomImageUrl : null;

          // 상단/하단 이미지가 아닌 URL만 필터링
          const contentImageUrls = detailImageUrls.filter(url => {
            // 상단/하단 이미지인지 확인
            const isTopOrBottomImage = (() => {
              // 1. URL 직접 비교
              if (url === fallbackTopImageUrl || url === fallbackBottomImageUrl) {
                return true;
              }

              // 2. 파일명에 topimage 또는 bottomimage 포함 여부 확인
              try {
                // URL에서 파일명 추출
                const urlObj = new URL(url);
                const pathname = urlObj.pathname;
                const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

                // 파일명에 topimage 또는 bottomimage 포함 여부 확인
                return filename.includes('topimage') || filename.includes('bottomimage');
              } catch (error) {
                // URL 파싱 오류 시 기존 방식 결과 반환
                return false;
              }
            })();

            // 상단/하단 이미지가 아닌 경우만 포함
            return !isTopOrBottomImage;
          });

          console.log(`폴백 HTML에 추가할 컨텐츠 이미지: ${contentImageUrls.length}개`);

          contentImageUrls.forEach((url, i) => {
            detailHtml += `
            <div style="margin:0; padding:0; text-align:center;">
              <img src="${url}" alt="상세 이미지 ${i+1}" style="max-width:100%; width:800px; display:block; margin:0 auto;">
            </div>
            `;
          });

          // 하단 이미지 추가 (있으면)
          if (settings.useBottomImage && settings.bottomImageUrl) {
            detailHtml += `
            <div style="margin:0; padding:0; text-align:center;">
              <img src="${settings.bottomImageUrl}" alt="하단 이미지" style="max-width:100%; width:800px; display:block; margin:0 auto;">
            </div>
            `;
          }

          detailHtml += '</div>';
        }
      } else {
        // 병합된 이미지가 성공적으로 생성된 경우
        console.log(`병합된 단일 이미지 생성 성공. URL: ${combinedImageUrl}...`);

        // 단일 S3 이미지 URL만 사용
        detailImageUrls = [combinedImageUrl];

        // HTML 생성 (단일 이미지용)
        detailHtml = `
        <div style="width:100%; max-width:800px; margin:0 auto; background-color:#ffffff; padding:0;">
          <div style="text-align:center;">
            <img src="${combinedImageUrl}" alt="상세 이미지" style="max-width:100%; width:800px; display:block; margin:0 auto;">
          </div>
        </div>
        `;
      }

      // 9. 부분 업데이트를 위한 필드 지정

      // 설정에서 상단/하단 이미지 URL 가져오기
      const imageSettings = store.state.settings &&
                      store.state.settings[0] &&
                      store.state.settings[0].defaultSettings &&
                      store.state.settings[0].defaultSettings.imageSettings || {};

      const mainTopImageUrl = imageSettings.useTopImage && imageSettings.topImageUrl ? imageSettings.topImageUrl : null;
      const mainBottomImageUrl = imageSettings.useBottomImage && imageSettings.bottomImageUrl ? imageSettings.bottomImageUrl : null;

      // 상단/하단 이미지를 제외한 처리된 이미지만 저장
      const filteredProcessedImages = allProcessedImages.filter(image => {
        // 이미지 URL이 없는 경우 제외
        if (!image || !image.url) return false;

        // 상단/하단 이미지인지 확인
        const isTopOrBottomImage = (() => {
          const imageUrl = image.url;

          // 1. URL 직접 비교
          if (imageUrl === mainTopImageUrl || imageUrl === mainBottomImageUrl) {
            return true;
          }

          // 2. 파일명에 topimage 또는 bottomimage 포함 여부 확인
          try {
            // URL에서 파일명 추출
            const urlObj = new URL(imageUrl);
            const pathname = urlObj.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

            // 파일명에 topimage 또는 bottomimage 포함 여부 확인
            return filename.includes('topimage') || filename.includes('bottomimage');
          } catch (error) {
            // URL 파싱 오류 시 기존 방식 결과 반환
            return false;
          }
        })();

        // 상단/하단 이미지가 아닌 경우만 포함
        return !isTopOrBottomImage;
      });

      console.log(`처리된 이미지 필터링: 전체 ${allProcessedImages.length}개 중 ${filteredProcessedImages.length}개 남음 (상단/하단 이미지 제외)`);

      // 번역 실패가 남았으면 '완료'가 아니라 '일부완료' — 전송 가드(=== 'completed')가 이 상품을 보내지 않는다
      const finalStatus = translationFailures.length > 0 ? 'partially_completed' : 'completed';
      const translationWarning = translationFailures.length > 0 ? {
        message: `상세이미지 ${detailImages.length}장 중 ${translationFailures.length}장 번역 실패 — 상세이미지 작업을 다시 돌려주세요`,
        failedCount: translationFailures.length,
        total: detailImages.length,
        failures: translationFailures,
        at: Date.now()
      } : null;

      // 번역 문구를 1688 속성·원문에 맞춰 고친 칸 — 상품에 남겨 화면·에이전트가 확인하게 한다(PR261016-11F5C4)
      const claimWarning = buildClaimWarning(filteredProcessedImages);

      const updateFields = {
        'detailClaimWarning': claimWarning,
        'detailImages': detailImageUrls,
        'detailHtml': detailHtml,
        'processedDetailImages': filteredProcessedImages,
        'detailImageStatus': finalStatus,
        'detailImageStatusObj': {
          status: finalStatus,
          images: detailImageUrls
        },
        'detailImageWarning': translationWarning,
        'originDetailImages': product.originDetailImages || detailImageUrls.map(url => ({
          originalUrl: url,
          img: url
        })),
        'lastModified': new Date(),
        'updatedAt': Date.now()
      };

      // 10. 상품 레벨의 coupangDoc 업데이트 (있는 경우)
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        // 모든 이미지 URL을 콤마로 구분하여 하나의 문자열로 결합
        const combinedDetailImageUrls = detailImageUrls.join(',');

        // 모든 jsonDocument 항목에 이미지 정보 적용
        product.coupangDoc.jsonDocument.forEach((docItem, docIndex) => {
          if (docItem.imagePage && docItem.imagePage.details) {
            // 상세 이미지 URL 업데이트 (점 표기법 사용)
            if (detailImageUrls && detailImageUrls.length > 0) {
              updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.details.detailedImage`] = combinedDetailImageUrls;
              updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.details.htmlProductDetailContent`] = "";
            }

            // altText 설정 및 길이 검증 (10자~40자)
            let finalAltText = initialAltText;

            // 길이 검증: 10자 미만이면 "상세페이지 이미지 참조" 추가
            if (finalAltText.length < 10) {
              finalAltText = finalAltText ? `${finalAltText} 상세페이지 이미지 참조` : '상세페이지 이미지 참조';
            }

            // 길이 검증: 40자 초과면 40자로 자름
            if (finalAltText.length > 40) {
              finalAltText = finalAltText.substring(0, 40);
            }

            updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.details.altText`] = finalAltText;
            console.log(`altText 최종 설정: "${finalAltText}" (${finalAltText.length}자)`);
          }
        });
      }

      // 11. 옵션 정보 업데이트
      if (product.options && product.options.length > 0) {
        console.log(`${product.options.length}개 옵션 업데이트 시작`);

        product.options.forEach((option, optionIndex) => {
          // 옵션의 이미지 설정
          updateFields[`options.${optionIndex}.detailImages`] = [...detailImageUrls];

          // 옵션 상태 업데이트 - 문자열 형식으로 설정 (AIRocketReg에서 사용)
          updateFields[`options.${optionIndex}.detailImageStatus`] = finalStatus;

          // 객체 형식도 유지 (호환성 유지)
          updateFields[`options.${optionIndex}.detailImageStatusObj`] = {
            status: finalStatus,
            images: [...detailImageUrls]
          };
        });
      }

      // 12. 서버에 부분 업데이트 요청
      console.log('서버 부분 업데이트 시작');
      await store.dispatch('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });
      console.log('Vuex 상품 업데이트 완료');

      // 13. 처리 완료 상태 및 결과 반환
      return {
        status: finalStatus,
        type: 'detailImageStatus',
        detailImages: detailImageUrls,
        warning: translationWarning
      };
    } catch (error) {
      console.error('상세 이미지 처리 및 저장 중 오류:', error);

      // 오류 세부 정보 로깅
      if (error.response) {
        console.error('API 응답 오류 상세:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }

      return {
        status: 'error',
        type: 'detailImageStatus',
        detailImageStatus: 'error', // 문자열 형식으로 추가
        error: error.message
      };
    } finally {
      // 로딩 상태 해제
      store.dispatch('setProcessing', {
        isProcessing: false,
        message: ''
      });
    }
  },

  /**
   * 모든 상세 이미지 AI 처리
   * @param {Array} imageUrls - 처리할 이미지 URL 배열
   * @returns {Promise<Array>} - 처리된 이미지 정보 배열
   */
  async processAllDetailImages(imageUrls) {
    if (!imageUrls || imageUrls.length === 0) {
      throw new Error('처리할 이미지가 없습니다.');
    }

    try {
      // 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '상세 이미지 처리 준비 중...'
      });

      // 상단/하단 이미지 URL 가져오기 (설정에서)
      const settings = store.state.settings &&
                      store.state.settings[0] &&
                      store.state.settings[0].defaultSettings &&
                      store.state.settings[0].defaultSettings.imageSettings || {};

      const processAllTopImageUrl = settings.useTopImage && settings.topImageUrl ? settings.topImageUrl : null;
      const processAllBottomImageUrl = settings.useBottomImage && settings.bottomImageUrl ? settings.bottomImageUrl : null;

      const processedImages = [];

      // 각 이미지 별로 처리
      for (let i = 0; i < imageUrls.length; i++) {
        const imageUrl = imageUrls[i];

        // 상단/하단 이미지인지 확인
        const isTopOrBottomImage = (() => {
          // 1. URL 직접 비교 (기존 방식)
          if (imageUrl === processAllTopImageUrl || imageUrl === processAllBottomImageUrl) {
            return true;
          }

          // 2. 파일명에 topimage 또는 bottomimage 포함 여부 확인 (추가 방식)
          try {
            // URL에서 파일명 추출
            const urlObj = new URL(imageUrl);
            const pathname = urlObj.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

            // 파일명에 topimage 또는 bottomimage 포함 여부 확인
            return filename.includes('topimage') || filename.includes('bottomimage');
          } catch (error) {
            // URL 파싱 오류 시 기존 방식 결과 반환
            return false;
          }
        })();

        // 상단/하단 이미지인 경우 처리 건너뛰기
        if (isTopOrBottomImage) {
          console.log(`이미지 ${i + 1}/${imageUrls.length}는 상단/하단 이미지이므로 AI 처리 건너뜀:`, imageUrl.substring(0, 50) + '...');
          continue;
        }

        // 진행 상태 업데이트
        store.dispatch('setProcessing', {
          isProcessing: true,
          message: `이미지 ${i + 1}/${imageUrls.length} 처리 중...`
        });

        try {
          // translateSingleImage 메서드 재사용
          const processedImage = await this.translateSingleImage(imageUrl, i);
          processedImages.push(processedImage);
        } catch (error) {
          console.error(`이미지 ${i + 1} 처리 중 오류:`, error);
          // 개별 이미지 오류 발생 시, 계속 진행
        }
      }

      // 처리된 이미지 정렬 (인덱스 기준)
      processedImages.sort((a, b) => a.index - b.index);

      return processedImages;
    } catch (error) {
      console.error('모든 이미지 처리 중 오류:', error);
      throw error;
    } finally {
      // 로딩 상태 해제
      store.dispatch('setProcessing', {
        isProcessing: false,
        message: ''
      });
    }
  }
};

export default detailImageService;